import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CheckinDialog from "@/components/CheckinDialog";
import KidCheckinDialog from "@/components/KidCheckinDialog";
import PetDialog from "@/components/PetDialog";
import PetCareDialog from "@/components/PetCareDialog";
import MembersManager from "@/components/MembersManager";
import HistoryDialog from "@/components/HistoryDialog";
import PinDialog from "@/components/PinDialog";
import FamilyPinDialog from "@/components/FamilyPinDialog";
import CalendarSheet from "@/components/CalendarSheet";
import ChoresSheet from "@/components/ChoresSheet";
import TodoListsSheet from "@/components/TodoListsSheet";
import EventPill from "@/components/EventPill";
import BirthdayBanner from "@/components/BirthdayBanner";
import ConfettiBurst from "@/components/ConfettiBurst";
import WhosFreeDialog from "@/components/WhosFreeDialog";
import BillingSheet from "@/components/BillingSheet";
import TrialNudgeBanner from "@/components/TrialNudgeBanner";
import WeekInReviewCard from "@/components/WeekInReviewCard";
import InstallPwaPrompt from "@/components/InstallPwaPrompt";
import PushRevokedBanner from "@/components/PushRevokedBanner";
import NotificationsOffBanner from "@/components/NotificationsOffBanner";
import InstallCelebrationBanner from "@/components/InstallCelebrationBanner";
import FunScreen from "@/components/FunScreen";
import HouseUnlockDialog from "@/components/HouseUnlockDialog";
import HouseFirstPinDialog from "@/components/HouseFirstPinDialog";
import HouseTiles from "@/components/HouseTiles";
import HouseDeletionBanner from "@/components/HouseDeletionBanner";
import BillingPastDueBanner from "@/components/BillingPastDueBanner";
import QuickGuideDialog from "@/components/QuickGuideDialog";
import MoodPeek from "@/components/MoodPeek";
import PetPeek from "@/components/PetPeek";
import BirthdayCountdown from "@/components/BirthdayCountdown";
import { useLongPress } from "@/lib/useLongPress";
import { isHouseUnlocked, getHouseUnlockPin, clearHouseUnlock } from "@/lib/houseLock";
import { houseEmoji } from "@/lib/houseEmoji";
import { useVisitPing } from "@/lib/useVisitPing";
import { atAGlance, computeVibe, moodEmoji, petWalkVibe } from "@/lib/vibe";
import { isYoungKid, isTeenPlusFamily, hasToddler, hasYoungKid, ageFromBirthday } from "@/lib/birthdays";
import { colorOf } from "@/lib/constants";
import { api, auth } from "@/lib/api";
import { mergeWithBirthdays, selectStripEvents, dayLabel } from "@/lib/events";
import { EVENT_KINDS } from "@/lib/events";
import { isDialogOpen } from "@/lib/dialogPolling";
import { clearAppBadge } from "@/lib/push";
import { getToddlerMemberId, clearToddlerMode, setToddlerMemberId } from "@/lib/toddlerMode";
import { isDeviceCalendarOnly, getDeviceDashboardMode } from "@/lib/deviceCalendarOnly";
import { getSettingsPinRequired } from "@/lib/settingsPinLock";
import { greetingFor } from "@/lib/greeting";
import { isPetWalkLockedForPet } from "@/lib/petCareGate";
import { shouldPulseWalkUpgrade, markWalkUpgradePulsed } from "@/lib/walkUpgradePulse";
import {
  WELCOME_GUIDE,
  DISCOVER_GUIDE,
  HOUSES_GUIDE,
  PETS_GUIDE,
  isGuideDismissed,
  dismissGuideSession,
  resetGuide,
} from "@/lib/guides";
import { Settings, Flame, X, Calendar as CalendarIcon, Tv, ListChecks, ListTodo, ArrowRight, Lock, Smile, PawPrint, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

/**
 * BigTile — a single member's hero tile on the dashboard. Extracted so
 * `useLongPress` can live per-tile. Long-press (~450ms) or right-click
 * on a non-pet tile fires `onPeek(member, rect)` to open the 7-day
 * mood peek; a plain tap still calls `onOpen` (the check-in dialog).
 */
function BigTile({ member: m, index: i, lockedOut, checkin, compactLayout, onOpen, onPeek, onLogWalkNow, onWalkUpgrade, walkLocked, todoStatus, onOpenTodos, tripInfo }) {
  const btnRef = useRef(null);
  const isPet = m.role === "pet";
  // Pulse the locked "Upgrade to walk" pill once per pet. Captured on
  // mount so the class is stable for the lifetime of this tile and we
  // immediately mark the pet as pulsed — even if the user never sees
  // the animation play out, we still respect the "max once" rule.
  const [walkPulse] = useState(() =>
    isPet && walkLocked && shouldPulseWalkUpgrade(m.id),
  );
  useEffect(() => {
    if (walkPulse) markWalkUpgradePulsed(m.id);
  }, [walkPulse, m.id]);
  const vibe = isPet ? null : computeVibe(checkin);
  const petVibe = isPet ? petWalkVibe(checkin) : null;
  const petWalks = isPet ? (checkin?.walks?.length ?? 0) : 0;
  const bg = (() => {
    if (isPet) {
      // Match humans: once a walk is logged the pet tile lights up with
      // the same green/amber/red tint their vibe suggests. Before any
      // walk we stay on the soft avatar-tinted paper so the pet's colour
      // still reads when nothing's logged yet.
      if (!petVibe || petVibe.level === "unknown") {
        return `color-mix(in srgb, var(--bg) 70%, ${colorOf(m.color).hex})`;
      }
      // Mood tiles read at full strength regardless of family ages —
      // a teen-plus household still wants the dashboard tiles to feel
      // alive (only the calendar grid is faded for teens, not the
      // people / pet check-in cards).
      return petVibe.tint;
    }
    if (!checkin) return "var(--bg-soft)";
    return vibe.tint;
  })();
  const longPressProps = useLongPress({
    disabled: lockedOut || !onPeek,
    onLongPress: () => {
      if (!onPeek) return;
      const rect = btnRef.current?.getBoundingClientRect?.() || null;
      onPeek(m, rect);
    },
    onClick: () => !lockedOut && onOpen && onOpen(),
  });
  return (
    <button
      ref={btnRef}
      type="button"
      disabled={lockedOut}
      {...longPressProps}
      className={`relative rise-in rise-in-${Math.min(i + 1, 5)} aspect-square rounded-[18px] p-4 md:p-5 ${isPet ? "pb-9 md:pb-10" : ""} flex flex-col items-center justify-center gap-2 border border-[color:var(--line-soft)] hover:scale-[1.02] active:scale-[0.98] transition-all ${lockedOut ? "opacity-40 pointer-events-none" : ""}`}
      style={{ background: bg }}
      data-testid={`big-tile-${m.id}`}
      aria-label={`Check in as ${m.name}${tripInfo ? `, away${tripInfo.backLabel ? ` until ${tripInfo.backLabel}` : ""}` : ""}`}
    >
      {/* "Away" pill — top-left corner when this member is tagged on
          a multi-day travel event that is currently active. Dims the
          avatar too so the tile reads as "not around right now" at a
          glance. `tripInfo` is computed on the dashboard with the
          already-loaded events list so this component stays pure. */}
      {tripInfo && !isPet && (
        <span
          className="absolute top-2 left-2 label-tiny inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[color:var(--line)] bg-[color:var(--bg)]/85 backdrop-blur-sm z-10"
          title={`Away${tripInfo.backLabel ? ` — back ${tripInfo.backLabel}` : ""}${tripInfo.title ? ` · ${tripInfo.title}` : ""}`}
          data-testid={`member-trip-away-${m.id}`}
        >
          <span aria-hidden="true">✈️</span>
          <span>Away</span>
        </span>
      )}
      {/* Lists chip — top-right corner. Renders only when this person
          has open list items tagged to them. Tapping it opens the
          shared lists sheet instead of the check-in dialog. */}
      {!isPet && todoStatus && todoStatus.open_total > 0 && (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onOpenTodos && onOpenTodos();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onOpenTodos && onOpenTodos();
            }
          }}
          className="absolute top-2 right-2 label-tiny inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[color:var(--line)] bg-[color:var(--bg)]/80 backdrop-blur-sm hover:bg-[color:var(--bg)] transition-colors cursor-pointer"
          title={
            todoStatus.open_today > 0
              ? `${todoStatus.open_today} list item(s) for today, ${todoStatus.open_total} open total`
              : `${todoStatus.open_total} list item(s) open`
          }
          data-testid={`member-todos-chip-${m.id}`}
        >
          {todoStatus.open_today > 0
            ? `${todoStatus.open_today} today · ${todoStatus.open_total} open`
            : `${todoStatus.open_total} open`}
        </span>
      )}
      {m.avatar_image ? (
        <img
          src={m.avatar_image}
          alt=""
          className={`${
            compactLayout
              ? "w-16 h-16 md:w-20 md:h-20"
              : "w-20 h-20 md:w-24 md:h-24"
          } rounded-full object-cover border-2 border-[color:var(--bg)] ${tripInfo && !isPet ? "opacity-55" : ""}`}
        />
      ) : (
        <span
          className={`${
            compactLayout
              ? "w-16 h-16 md:w-20 md:h-20 text-4xl md:text-5xl"
              : "w-20 h-20 md:w-24 md:h-24 text-5xl md:text-6xl"
          } rounded-full bg-[color:var(--bg)] border-2 border-[color:var(--bg)] flex items-center justify-center leading-none ${tripInfo && !isPet ? "opacity-55" : ""}`}
          aria-hidden="true"
        >
          {m.avatar_emoji || "🙂"}
        </span>
      )}
      <div
        className={`font-display leading-none tracking-tight text-center text-[color:var(--accent)] ${
          compactLayout ? "text-lg md:text-xl" : "text-xl md:text-2xl"
        }`}
      >
        {m.name}
      </div>
      {tripInfo && !isPet && (
        <div className="label-tiny text-[color:var(--ink-3)] text-center leading-tight px-2">
          {tripInfo.backLabel ? `Back ${tripInfo.backLabel}` : "Away"}
        </div>
      )}
      <div className="label-tiny flex items-center gap-1.5">
        {isPet ? (
          (() => {
            // Walks make sense for dogs and (rarely) cats. For everything
            // else — turtles, fish, hamsters, birds, snakes — "No walk
            // yet" is nonsense. Show feeds/water status instead, or
            // fall back to silence so the card stays clean.
            const species = (m.pet_species || "").toLowerCase();
            const walksRelevant =
              species === "dog" || species === "cat" || !species;
            if (walksRelevant) {
              if (petWalks <= 0) return <span>No walk yet</span>;
              // Count how many of the recorded walks happened today
              // (local clock). The `walks` array can carry yesterday's
              // late-evening walk into this morning, so saying "2
              // walks today" when one was at 22:30 yesterday is wrong.
              // Drop the "today" suffix in that case.
              const todayKey = new Date().toDateString();
              const walksToday = (checkin?.walks || []).filter((w) => {
                if (!w?.at) return false;
                const d = new Date(w.at);
                return !Number.isNaN(d.getTime()) && d.toDateString() === todayKey;
              }).length;
              const labelToday = walksToday > 0;
              const count = labelToday ? walksToday : petWalks;
              return (
                <span>
                  {count} walk{count === 1 ? "" : "s"}{labelToday ? " today" : ""}
                </span>
              );
            }
            const feeds =
              (checkin?.feeds?.length ?? 0) + (checkin?.fed_at ? 1 : 0);
            const water =
              (checkin?.water_changes?.length ?? 0) +
              (checkin?.water_change_at ? 1 : 0);
            if (feeds > 0) {
              return <span>{feeds} feed{feeds === 1 ? "" : "s"} today</span>;
            }
            if (water > 0) {
              return <span>Fresh water today</span>;
            }
            return <span>Not yet</span>;
          })()
        ) : checkin ? (
          <>
            <span aria-hidden="true" className="text-base leading-none">
              {moodEmoji(checkin.mood)}
            </span>
            <span>Mood {checkin.mood}</span>
          </>
        ) : (
          <span>Not yet</span>
        )}
      </div>
      {/* Walk-now shortcut — sits at bottom of the pet tile so the
          everyday "I just walked the dog" tap doesn't require opening
          the full PetDialog and scrolling. Only renders for dog/cat-
          like pets where walks make sense, and stops click
          propagation so a tap doesn't also open the pet sheet
          underneath. Stays hidden when the tile is locked-out. When
          the pet is over the plan's walk-tier cap we swap the green
          "Walk now" pill for a muted lock pill that opens the upgrade
          sheet instead — keeps the affordance visible so the family
          knows the shortcut exists, just gated. */}
      {isPet && (onLogWalkNow || onWalkUpgrade) && !lockedOut && (() => {
        const species = (m.pet_species || "").toLowerCase();
        const walksRelevant =
          species === "dog" || species === "cat" || !species;
        if (!walksRelevant) return null;
        if (walkLocked) {
          return (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onWalkUpgrade && onWalkUpgrade(m);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onWalkUpgrade && onWalkUpgrade(m);
                }
              }}
              className={`absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[color:var(--ink-3)]/80 hover:bg-[color:var(--ink-3)] text-[#fcfcfa] text-[11px] uppercase tracking-[0.06em] font-semibold whitespace-nowrap shadow-md transition-all cursor-pointer inline-flex items-center gap-1${walkPulse ? " ff-walk-upgrade-pulse" : ""}`}
              data-testid={`big-tile-walk-locked-${m.id}`}
              aria-label={`Upgrade to log walks for ${m.name}`}
              title={`Upgrade to log walks for ${m.name}`}
            >
              <Lock className="w-3 h-3" />
              Upgrade to walk
            </span>
          );
        }
        return (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onLogWalkNow(m);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onLogWalkNow(m);
              }
            }}
            className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[color:var(--accent)] hover:brightness-110 text-[#fcfcfa] text-[11px] uppercase tracking-[0.06em] font-semibold whitespace-nowrap shadow-md transition-all cursor-pointer"
            data-testid={`big-tile-walk-now-${m.id}`}
            aria-label={`Log a walk now for ${m.name}`}
          >
            🐾 Walk now
          </span>
        );
      })()}
    </button>
  );
}

export default function Dashboard({
  settings,
  members,
  checkins,
  onRefresh,
  setCheckins,
  setMembers,
  setSettings,
  viewerId,
  setViewerId,
  onSignOut,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang } = useI18n();
  // Deep-link from EventInfoPage's "Open in calendar" button: when
  // location.state carries `calendarScrollToEventId`, scroll to that
  // event's day. For mood-front families this opens the modal sheet;
  // for calendar-front families the inline sheet is already on screen
  // — we just feed it the scrollToEventId and skip the modal so we
  // don't double-mount (which races and surfaces "Could not load
  // events"). Consumed once then cleared so refresh doesn't replay.
  //
  // Same effect handles `openListId` for the "Linked list" card on
  // EventInfoPage — opens the Todo Lists modal pre-scrolled to that
  // list. Both keys are independent so a future deep-link can mix
  // them.
  useEffect(() => {
    const evId = location.state?.calendarScrollToEventId;
    const editEvId = location.state?.calendarOpenEditEventId;
    const requestDelete = !!location.state?.calendarRequestDelete;
    const listId = location.state?.openListId;
    if (!evId && !editEvId && !listId) return;
    if (evId) {
      setCalendarScrollTo(evId);
      setShowCalendar(true);
    }
    if (editEvId) {
      // `calendarRequestDelete` reuses the same event-id signal but
      // tells the calendar to fire its delete-scope picker instead of
      // opening the editor. Comes from EventInfoPage's Delete button
      // on a recurring instance.
      if (requestDelete) {
        setCalendarRequestDelete(editEvId);
      } else {
        setCalendarOpenEdit(editEvId);
      }
      setShowCalendar(true);
    }
    if (listId) {
      setTodoListsInitialId(listId);
      setShowTodoLists(true);
    }
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [pinFor, setPinFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [showManager, setShowManager] = useState(false);
  const [managerAutoAdd, setManagerAutoAdd] = useState(false);
  // One in-app visit ping per session — lets admin stats separate
  // "people looking at the marketing" from "families actually using the
  // app today". Defers, sessionStorage-guarded inside the hook.
  useVisitPing("app");
  // On a shared iPad we re-prompt every time Settings is reopened so a kid
  // can't stumble in after a parent closed it.
  const [familyPinPrompt, setFamilyPinPrompt] = useState(null); // { onSuccess }
  const [showCalendar, setShowCalendar] = useState(false);
  // When the user taps a specific upcoming-event card we remember which
  // event that was, so the CalendarSheet can scroll to its day group on
  // open instead of dumping the user at the top of the list.
  const [calendarScrollTo, setCalendarScrollTo] = useState(null);
  // When EventInfoPage's Edit button navigates back to /app with a
  // `calendarOpenEditEventId` in location.state, we stash it here and
  // pass to CalendarSheet so it opens the edit drawer for that event
  // as soon as events load. Cleared once consumed so refresh doesn't
  // re-open the editor.
  const [calendarOpenEdit, setCalendarOpenEdit] = useState(null);
  // Same signal-id pattern, but tells CalendarSheet to fire its
  // delete-scope picker for recurring instances instead of the editor.
  const [calendarRequestDelete, setCalendarRequestDelete] = useState(null);
  const [showChores, setShowChores] = useState(false);
  const [showTodoLists, setShowTodoLists] = useState(false);
  // When opening the lists sheet from a linked-event chip we want it
  // to land directly on the matching list's detail view, not the
  // index. The id is consumed once on mount of TodoListsSheet.
  const [todoListsInitialId, setTodoListsInitialId] = useState(null);

  // "What's actually new" calendar dot — surfaces a tiny indicator on
  // the calendar nav icon when the family calendar has been edited
  // since this device last opened it. Stored in localStorage so it
  // works without per-user accounts (FamFeel families share a single
  // code+PIN; each phone / iPad has its own stamp).
  const [calendarHasUnseen, setCalendarHasUnseen] = useState(false);
  const seenStorageKey = useMemo(() => {
    const fid = auth.getFamilyId() || "anon";
    return `famfeel_calendar_seen_at_${fid}`;
  }, []);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const res = await api.calendarChanged(null);
        if (!alive) return;
        const lastChanged = res?.events_last_changed_at || null;
        if (!lastChanged) {
          setCalendarHasUnseen(false);
          return;
        }
        let lastSeen = null;
        try { lastSeen = localStorage.getItem(seenStorageKey); } catch { /* private mode */ }
        setCalendarHasUnseen(!lastSeen || lastChanged > lastSeen);
      } catch {
        // best-effort; the dot is purely cosmetic
      }
    };
    tick();
    const t = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [seenStorageKey]);
  // When opening the calendar, mark this device as having seen it.
  // Done eagerly client-side so the dot disappears immediately. The
  // backend's `last_seen_calendar_at` member-level stamp is kept in
  // sync best-effort for any future per-member surfaces, but the
  // device-local stamp is the source of truth for the dot itself.
  useEffect(() => {
    if (!showCalendar) return;
    setCalendarHasUnseen(false);
    try {
      localStorage.setItem(seenStorageKey, new Date().toISOString());
    } catch { /* private mode */ }
    if (viewerId) {
      api.calendarSeen(viewerId).catch(() => {/* best-effort */});
    }
  }, [showCalendar, viewerId, seenStorageKey]);
  const openCalendarAndMarkSeen = (scrollTo = null) => {
    if (scrollTo !== null) setCalendarScrollTo(scrollTo);
    setShowCalendar(true);
  };

  // Toddler mode: per-device lock that pins the iPad to one kid's check-in.
  // `toddlerId` is the member id of the pinned toddler, or null when unlocked.
  const [toddlerId, setToddlerId] = useState(() => getToddlerMemberId());
  const [showUnlock, setShowUnlock] = useState(false);
  const [funOpen, setFunOpen] = useState(false);
  const isToddlerMode = !!toddlerId && members.some((m) => m.id === toddlerId);
  // Renderable JSX for the toddler "tap for a surprise" picker. Kept
  // here so we can drop it into multiple positions — it lives in the
  // member-grid slot on full dashboards, but on calendar-only toddler
  // mode it belongs above the event strip (toddlers care about surprise
  // > upcoming events).
  const toddlerFunPicker = (
    <div className="mb-6" data-testid="toddler-fun-picker">
      <div className="label-tiny text-center mb-3 text-[color:var(--ink-3)]">
        Tap for a surprise
      </div>
      <div className="grid grid-cols-4 gap-3 md:gap-5 max-w-5xl mx-auto">
        {[
          { v: "butterflies", emoji: "🦋", label: "Butterflies" },
          { v: "bubbles", emoji: "🫧", label: "Bubbles" },
          { v: "balls", emoji: "⚽️", label: "Balls" },
          { v: "fish", emoji: "🐠", label: "Fish" },
        ].map((p) => (
          <button
            key={p.v}
            type="button"
            onClick={() => setFunOpen(p.v)}
            // Softer surface — the 2002-era hard-shadow + black
            // border read as toy-box loud once toddler mode also
            // had to sit alongside the rest of the dashboard. Now:
            // a hairline border, a gentle drop shadow on the card
            // surface, and a subtle press-in feel on hover/tap.
            className="aspect-square rounded-[20px] border border-[color:var(--line)] bg-[color:var(--surface)] shadow-sm hover:shadow active:translate-y-[2px] transition-all flex items-center justify-center"
            data-testid={`toddler-fun-${p.v}`}
            aria-label={p.label}
          >
            <span
              className="leading-none"
              style={{ fontSize: "clamp(64px, 14vw, 160px)" }}
              aria-hidden="true"
            >
              {p.emoji}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
  const [billing, setBilling] = useState({ open: false, reason: null });
  const [tier, setTier] = useState(null);
  // Mood peek — long-press / right-click a member card to see their
  // last 7 days of mood in a small anchored popover.
  const [peekFor, setPeekFor] = useState(null); // { member, rect }
  // Birthday countdown overlay — toddler-only exception to the
  // no-click rule in calendar-only toddler mode. Holds { member, age,
  // daysUntil } when open.
  const [bdayCountdown, setBdayCountdown] = useState(null);

  // Quick-guide walkthroughs (welcome + houses). `openGuide` holds the
  // active guide object or null. Guides listen for a global
  // `famfeel:open-guide` CustomEvent so Settings ("Show quick guide"
  // buttons) and post-checkout toasts can open them from anywhere.
  const [openGuide, setOpenGuide] = useState(null);
  useEffect(() => {
    const onOpen = (e) => {
      const which = e?.detail?.id;
      if (which === "welcome") setOpenGuide(WELCOME_GUIDE);
      else if (which === "discover") setOpenGuide(DISCOVER_GUIDE);
      else if (which === "houses") setOpenGuide(HOUSES_GUIDE);
      else if (which === "pets") setOpenGuide(PETS_GUIDE);
    };
    window.addEventListener("famfeel:open-guide", onOpen);
    return () => window.removeEventListener("famfeel:open-guide", onOpen);
  }, []);

  // Cross-component "open billing sheet" trigger. Used by feature
  // gates that live deep in the tree (e.g. the event attachment
  // picker on Free) so they don't need a callback prop drilled
  // through every CalendarSheet/EventInfoPage caller.
  useEffect(() => {
    const onOpen = (e) => {
      const reason = e?.detail?.reason || null;
      setBilling({ open: true, reason });
    };
    window.addEventListener("famfeel:open-billing", onOpen);
    return () => window.removeEventListener("famfeel:open-billing", onOpen);
  }, []);

  // Auto-show the Welcome guide ONLY on the device that just created the
  // account (flag set in Onboarding.js). Existing users are never
  // interrupted — they can re-open it from Settings → "Show Welcome
  // guide" any time. A brand-new family on this device should ALWAYS
  // see the guide, even if the device previously dismissed it for a
  // different family — so we reset the dismissal record before
  // opening, otherwise testers / serial creators would silently miss
  // the welcome flow on every subsequent signup.
  useEffect(() => {
    if (!settings) return;
    if (openGuide) return;
    let newAccount = false;
    try {
      newAccount = localStorage.getItem("famfeel_new_account_v1") === "1";
    } catch {
      /* privacy-mode — just skip */
    }
    if (!newAccount) return;
    try {
      localStorage.removeItem("famfeel_new_account_v1");
    } catch {}
    // Wipe any stale dismissal so the new family sees the guide fresh,
    // even on a device that already dismissed it for a previous family.
    try {
      resetGuide(WELCOME_GUIDE.id);
    } catch {}
    // Small delay so the dashboard has a chance to paint first — the
    // guide lands on a fully-rendered app, not a blank frame.
    const t = setTimeout(() => setOpenGuide(WELCOME_GUIDE), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.id]);

  // Houses quick-guide toast — fires ONLY on the device that just
  // completed an Extended-tier upgrade (flag set by the Stripe return
  // handler above). Existing Extended families aren't nagged. Guide is
  // still re-openable any time from Settings → "Show Houses guide".
  useEffect(() => {
    if (tier !== "extended" && tier !== "grand") return;
    let justUpgraded = false;
    try {
      justUpgraded = localStorage.getItem("famfeel_new_houses_v1") === "1";
    } catch {}
    if (!justUpgraded) return;
    try {
      localStorage.removeItem("famfeel_new_houses_v1");
    } catch {}
    if (isGuideDismissed(HOUSES_GUIDE.id)) return;
    let dismissed = false;
    const id = toast("Houses are live on your plan", {
      description:
        "Blended family? Multi-household? Tap to see how Houses works.",
      duration: 12000,
      action: {
        label: "Open guide",
        onClick: () => {
          dismissed = true;
          setOpenGuide(HOUSES_GUIDE);
        },
      },
      cancel: {
        label: "Later",
        onClick: () => {
          dismissed = true;
          dismissGuideSession(HOUSES_GUIDE.id);
        },
      },
      onAutoClose: () => {
        if (!dismissed) dismissGuideSession(HOUSES_GUIDE.id);
      },
      onDismiss: () => {
        if (!dismissed) dismissGuideSession(HOUSES_GUIDE.id);
      },
    });
    return () => toast.dismiss(id);
  }, [tier]);

  // Pets-only quick-guide — fires ONCE per family when the dashboard
  // mode flips to "pets" and the user hasn't already dismissed the
  // guide. Re-openable from Settings → "Show pets guide" any time.
  // The check uses a localStorage flag so switching back-and-forth
  // doesn't keep re-prompting; isGuideDismissed acts as a second
  // guard for users who already tapped "Got it".
  useEffect(() => {
    if (!settings) return;
    if (openGuide) return;
    const mode = settings?.dashboard_mode || "full";
    if (mode !== "pets") return;
    if (isGuideDismissed(PETS_GUIDE.id)) return;
    let alreadyShown = false;
    try {
      alreadyShown = localStorage.getItem("famfeel_pets_guide_shown_v1") === "1";
    } catch {}
    if (alreadyShown) return;
    try {
      localStorage.setItem("famfeel_pets_guide_shown_v1", "1");
    } catch {}
    const t = setTimeout(() => setOpenGuide(PETS_GUIDE), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.dashboard_mode]);

  // Open the billing sheet on mount if PetsPage (or another route) set a
  // pending flag — e.g. after tapping "Upgrade to Plus" on a locked pet
  // care row. Reading sessionStorage once is enough; we clear it so a
  // hard refresh doesn't re-open the sheet.
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem("ff_open_billing");
      if (reason) {
        sessionStorage.removeItem("ff_open_billing");
        setBilling({ open: true, reason });
      }
    } catch {}
  }, []);

  // Fetch tier once on mount and whenever the billing sheet closes
  // (which is when a user typically just completed a checkout). Used to
  // gate the Pet Care schedule features for pets beyond the tier cap.
  useEffect(() => {
    let alive = true;
    api
      .billingStatus()
      .then((s) => {
        if (alive) setTier(s?.tier || "free");
      })
      .catch(() => {
        if (alive) setTier("free");
      });
    return () => {
      alive = false;
    };
  }, [billing.open]);
  // Re-fetch members/checkins/events when the house-unlock set changes
  // on this device. The server filters based on the `X-House-Unlocks`
  // header, so unlocking a house means more content is now visible.
  useEffect(() => {
    const onChange = async () => {
      try {
        const ms = await api.listMembers();
        if (Array.isArray(ms)) setMembers(ms);
      } catch {
        /* noop */
      }
      try {
        const cs = await api.todayCheckins({
          viewer_id: localStorage.getItem("famfeel_viewer_id") || undefined,
        });
        if (Array.isArray(cs)) setCheckins(cs);
      } catch {
        /* noop */
      }
      try {
        const hs = await api.listHouses();
        if (Array.isArray(hs)) setHouses(hs);
      } catch {
        /* noop */
      }
    };
    window.addEventListener("famfeel:house-unlocks-changed", onChange);
    return () =>
      window.removeEventListener("famfeel:house-unlocks-changed", onChange);
  }, []);
  const [streak, setStreak] = useState(null);
  // State for the house unlock prompt when a locked-house member is tapped.
  const [houseUnlockFor, setHouseUnlockFor] = useState(null);
  // First-time house PIN setup. The first person to "enter" a house
  // that has no PIN yet gets this dialog — forced to lock the front
  // door before they (or anyone) can step inside.
  const [houseFirstPinFor, setHouseFirstPinFor] = useState(null);
  // Active house on this device — null means we're on the front
  // "which house?" screen. Persisted to localStorage so a refresh
  // drops you back where you were.
  const [activeHouseId, setActiveHouseIdState] = useState(() => {
    try {
      return localStorage.getItem("ff_active_house") || null;
    } catch {
      return null;
    }
  });
  const setActiveHouseId = (id) => {
    // Lock the door behind you whenever you leave or switch houses.
    // Means every house tap needs the PIN again, so one person can't
    // hop between houses on a shared device without re-authenticating.
    // Skips when the target house is already unlocked (we're entering
    // the same house they just unlocked, e.g. via enterHouse).
    setActiveHouseIdState((prev) => {
      if (prev && prev !== id) clearHouseUnlock(prev);
      return id;
    });
    try {
      if (id) localStorage.setItem("ff_active_house", id);
      else localStorage.removeItem("ff_active_house");
    } catch {/* private mode, ignore */}
    // Fire a synthetic event so the per-house dashboard_mode override
    // picker above (which can't subscribe to `activeHouseId` state from
    // an earlier scope) swaps layouts immediately.
    try {
      window.dispatchEvent(new Event("famfeel:active-house-changed"));
    } catch {/* non-browser env */}
  };
  const [upcoming, setUpcoming] = useState([]);
  // Counter incremented on every `famfeel:celebrate` CustomEvent —
  // used as a key on <ConfettiBurst> so a fresh burst replays each
  // time the birthday banner is tapped, even if the component is
  // already mounted.
  const [celebrateKey, setCelebrateKey] = useState(0);
  // "Who's free?" quick-look dialog — answers "is everyone around
  // Saturday at 2?" by binning members into Free / Busy from the
  // already-loaded events. No network calls, just cross-references.
  const [showWhosFree, setShowWhosFree] = useState(false);
  useEffect(() => {
    const onCelebrate = () => setCelebrateKey((k) => k + 1);
    window.addEventListener("famfeel:celebrate", onCelebrate);
    return () => window.removeEventListener("famfeel:celebrate", onCelebrate);
  }, []);
  const [houses, setHouses] = useState([]);
  // To-do lists that are either (a) within 2 days of their own
  // `due_date`, or (b) linked to an event within 2 days — and aren't
  // fully ticked off yet. Surfaced as small "don't-miss-this" cards
  // on the dashboard so a packing list before a trip doesn't get
  // forgotten. Refreshed whenever upcoming events refresh.
  const [missableLists, setMissableLists] = useState([]);
  // Tracks whether the initial houses fetch has completed. Without it,
  // the stale-activeHouseId cleanup below would clear the in-progress
  // "inside a house" state on every mount (the initial empty array
  // never contains the active house id), bouncing you back to the
  // Houses front every time you return from /display or similar.
  const [housesLoaded, setHousesLoaded] = useState(false);
  const [chores, setChores] = useState([]);
  const [todosByMember, setTodosByMember] = useState({});

  // Load chores + refresh when Chores sheet closes / check-ins change
  useEffect(() => {
    api.listChores().then(setChores).catch(() => setChores([]));
  }, [members.length, showChores]);

  // Per-member open-todo counts. Refresh when the lists sheet closes so a
  // newly-tagged item is reflected on the dashboard chip without a reload.
  useEffect(() => {
    api.todosByMember().then(setTodosByMember).catch(() => setTodosByMember({}));
  }, [members.length, showTodoLists]);

  // Per-member chore counts for today
  const choresByMember = useMemo(() => {
    const map = new Map();
    for (const c of chores) {
      if (!c.due_today) continue;
      const cur = map.get(c.assignee_id) || { total: 0, done: 0 };
      cur.total += 1;
      if (c.completed_today) cur.done += 1;
      map.set(c.assignee_id, cur);
    }
    return map;
  }, [chores]);

  // "Chores" card — first 2 incomplete chores due today (so people
  // see what's coming) plus chores completed in the last 30 min so
  // the family gets a brief "yes, that got done" beat without the
  // card turning into a strike-through history. Anything older is
  // still visible inside the Chores sheet; it just leaves the front
  // card. Incomplete chores stop being eligible the moment the
  // server's `due_today` flag flips false at family-local midnight
  // (the backend computes it in the family's timezone, so any
  // member's device, anywhere, sees the same rollover). The
  // midnight-refresh effect below pokes `api.listChores()` shortly
  // after device-local midnight so the change actually lands
  // without waiting for the next user interaction.
  // Both halves cap at 2 so the card never grows past 4 rows.
  const upcomingChores = useMemo(() => {
    const sortKey = (c) => {
      if (!c.due_time) return "99:99";
      return c.due_time;
    };
    const dueList = (chores || [])
      .filter((c) => c.due_today && !c.completed_today && c.active !== false)
      .sort((a, b) => {
        const k = sortKey(a).localeCompare(sortKey(b));
        if (k !== 0) return k;
        return (a.title || "").localeCompare(b.title || "");
      })
      .slice(0, 2);
    const thirtyMinAgo = Date.now() - 30 * 60 * 1000;
    const doneList = (chores || [])
      .filter((c) => {
        if (!c.completed_today || !c.completed_at) return false;
        const t = Date.parse(c.completed_at);
        return Number.isFinite(t) && t >= thirtyMinAgo;
      })
      .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""))
      .slice(0, 2);
    return [...dueList, ...doneList];
  }, [chores]);

  // Refresh the chores list shortly after the family's local midnight
  // so:
  //   · "completed today" rows drop off (`due_today` flips false for
  //     non-recurring chores, completion `date` no longer matches)
  //   · daily/weekly chores reappear as "due today" for the new day
  // We use the device's local clock to compute the next midnight; the
  // server then re-derives `due_today` from the FAMILY's timezone,
  // which is the authoritative source. Margin: +5s past midnight so
  // we don't race the server's date rollover. Re-scheduling happens
  // recursively after each refresh so the next day is also covered
  // without the user reloading.
  useEffect(() => {
    let cancelled = false;
    const schedule = () => {
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 5, 0); // tomorrow 00:00:05 local
      const delay = Math.max(1000, nextMidnight - now);
      const id = setTimeout(async () => {
        if (cancelled) return;
        try {
          const fresh = await api.listChores();
          setChores(fresh);
        } catch {
          // Ignore — the next user-triggered fetch will heal it.
        }
        schedule();
      }, delay);
      return id;
    };
    const id = schedule();
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, []);

  // Load houses once (fails silently for non-extended families → empty list → flat grid)
  useEffect(() => {
    api.listHouses()
      .then((hs) => {
        const list = hs || [];
        setHouses(list);
        // Build a houseId → dashboard_mode map so the top-of-render
        // dashboardMode calc can pick up each house's preferred layout
        // without waiting for another fetch.
        const map = {};
        for (const h of list) {
          if (h.dashboard_mode) map[h.id] = h.dashboard_mode;
        }
        setHouseModeById(map);
        setHousesLoaded(true);
      })
      .catch(() => {
        setHouses([]);
        setHousesLoaded(true);
      });
  }, [members.length]);

  const checkinByMember = useMemo(() => {
    const map = {};
    for (const c of checkins) map[c.member_id] = c;
    return map;
  }, [checkins]);

  // Scope the "X of Y checked in today" headline and the at-a-glance
  // phrase to the current house when we're inside one. Mum's iPad
  // inside Mum's house should read "2 of 3 checked in" for Mum's
  // house, not "2 of 8" counting the whole extended family.
  const scopedMembers = useMemo(() => {
    if (!activeHouseId) return members;
    return members.filter(
      (m) =>
        m.house_id === activeHouseId ||
        // Include shared kids/pets that are visible to every house so
        // the count reflects who actually shows up on screen.
        ((m.role === "kid" || m.role === "pet") &&
          ((m.cross_house_privacy || {}).mood !== false ||
            (m.cross_house_privacy || {}).status !== false)),
    );
  }, [members, activeHouseId]);
  const phrase = useMemo(
    () => atAGlance(scopedMembers, checkins),
    [scopedMembers, checkins],
  );

  // Calendar-only mode — family uses FamFeel as a shared calendar only.
  // Human member cards + "X of Y checked in" headline + mood phrase all
  // hide; pets are shown unless the family also opted to hide them.
  // Dashboard mode: per-device override > family-wide setting > "full".
  // Values: "full" | "calendar" | "pets"
  const [deviceMode, setDeviceModeState] = useState(getDeviceDashboardMode());
  useEffect(() => {
    const onChange = () => setDeviceModeState(getDeviceDashboardMode());
    window.addEventListener("famfeel:device-dashboard-mode-changed", onChange);
    window.addEventListener("famfeel:device-calendar-only-changed", onChange);
    return () => {
      window.removeEventListener("famfeel:device-dashboard-mode-changed", onChange);
      window.removeEventListener("famfeel:device-calendar-only-changed", onChange);
    };
  }, []);
  const familyMode =
    settings?.dashboard_mode ||
    (settings?.calendar_only ? "calendar" : "full");
  // Active house override. When we're inside a house that has its own
  // dashboard_mode set, use that instead of the family default — one
  // house can live in calendar-only, another in full mood mode,
  // without stepping on each other.
  const [houseModeById, setHouseModeById] = useState({});
  const [activeHouseIdForMode, setActiveHouseIdForMode] = useState(() => {
    try {
      return localStorage.getItem("ff_active_house") || null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    const onChange = () => {
      try {
        setActiveHouseIdForMode(localStorage.getItem("ff_active_house") || null);
      } catch {
        setActiveHouseIdForMode(null);
      }
    };
    window.addEventListener("famfeel:active-house-changed", onChange);
    return () => window.removeEventListener("famfeel:active-house-changed", onChange);
  }, []);
  const houseOverride = activeHouseIdForMode
    ? houseModeById[activeHouseIdForMode] || null
    : null;
  const dashboardMode = deviceMode || houseOverride || familyMode;
  const calendarOnly = dashboardMode === "calendar";
  const petsOnly = dashboardMode === "pets";
  const calendarOnlyHidePets = !!settings?.calendar_only_hide_pets;
  const visibleMembers = useMemo(() => {
    if (petsOnly) return members.filter((m) => m.role === "pet");
    if (calendarOnly) {
      if (calendarOnlyHidePets) return [];
      return members.filter((m) => m.role === "pet");
    }
    return members;
  }, [members, calendarOnly, petsOnly, calendarOnlyHidePets]);

  // Filters for the calendar-only grid — mirror the Who/Kind dropdowns
  // in the full CalendarSheet so families can narrow the grid the same
  // way without opening the sheet.
  const [coMemberFilter, setCoMemberFilter] = useState("all");
  const [coKindFilter, setCoKindFilter] = useState("all");
  // True when the user tapped "Add event" from calendar-only mode — the
  // CalendarSheet opens with its add form pre-expanded.
  const [calendarAutoAdd, setCalendarAutoAdd] = useState(false);
  const viewer = members.find((m) => m.id === viewerId);
  const compactLayout = useMemo(() => isTeenPlusFamily(members), [members]);

  // Any parent-only action on a shared device should be gated by the family PIN.
  // Default: always ask. Trusted personal devices can opt out via
  // Settings → This device → "Require PIN to open Settings".
  const requireFamilyPin = (fn) => {
    if (!getSettingsPinRequired()) {
      fn && fn();
      return;
    }
    setFamilyPinPrompt({ onSuccess: fn });
  };

  const loadUpcoming = () =>
    api.upcomingEvents(20).then(setUpcoming).catch(() => {});

  // Pull to-do lists, pair them with upcoming events, and keep only
  // the "you'll want to look at this soon" set. Conditions:
  //   • list has its own due_date within the next 2 days, OR
  //   • list is linked to an event starting within the next 2 days
  // AND the list isn't fully ticked (done_count < item_count).
  // We fetch both in parallel. Errors are silent — the dashboard
  // already tolerates missing data everywhere else.
  const loadMissableLists = async () => {
    try {
      const [lists, events] = await Promise.all([
        api.listTodoLists().catch(() => []),
        api.upcomingEvents(50).catch(() => []),
      ]);
      const now = new Date();
      const horizon = new Date();
      horizon.setDate(now.getDate() + 2);
      horizon.setHours(23, 59, 59, 999);

      // Map list.id → nearest upcoming event (if any) whose
      // `linked_list_id` points to it.
      const eventByListId = new Map();
      for (const ev of events) {
        if (!ev?.linked_list_id) continue;
        const d = ev.starts_at ? new Date(ev.starts_at) : null;
        if (!d || isNaN(d.getTime())) continue;
        const prev = eventByListId.get(ev.linked_list_id);
        if (!prev || d < new Date(prev.starts_at)) {
          eventByListId.set(ev.linked_list_id, ev);
        }
      }

      const keep = [];
      for (const list of lists || []) {
        const done = Number(list.done_count || 0);
        const total = Number(list.item_count || 0);
        // Fully ticked → skip. (An empty list with 0 items is NOT
        // skipped here — that's the "oh I forgot to even start the
        // packing list" case, which we should surface loudest.)
        if (total > 0 && done >= total) continue;
        // Reason A: list has its own due_date within 2 days (past ok —
        // if it's overdue, still surface it).
        let dueDate = null;
        if (list.due_date) {
          const d = new Date(`${list.due_date}T23:59:59`);
          if (!isNaN(d.getTime()) && d <= horizon) {
            dueDate = d;
          }
        }
        // Reason B: linked to an event starting within 2 days.
        const linkedEv = eventByListId.get(list.id);
        let linkedEventDate = null;
        if (linkedEv) {
          const d = new Date(linkedEv.starts_at);
          if (!isNaN(d.getTime()) && d <= horizon) {
            linkedEventDate = d;
          }
        }
        if (!dueDate && !linkedEventDate) continue;
        // Sort key = soonest of the two.
        const when = dueDate && linkedEventDate
          ? (dueDate < linkedEventDate ? dueDate : linkedEventDate)
          : (dueDate || linkedEventDate);
        keep.push({
          list,
          event: linkedEventDate ? linkedEv : null,
          when,
          reason: linkedEventDate ? "event" : "due_date",
        });
      }
      keep.sort((a, b) => a.when - b.when);
      setMissableLists(keep);
    } catch {
      setMissableLists([]);
    }
  };

  useEffect(() => {
    api.familyStreak().then(setStreak).catch(() => {});
  }, [checkins.length]);

  useEffect(() => {
    loadUpcoming();
    loadMissableLists();
    const t = setInterval(() => {
      // Don't re-fetch while a dialog is open or the user is typing —
      // prevents mobile keyboard collapse during note entry.
      if (isDialogOpen()) return;
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      loadUpcoming();
      loadMissableLists();
    }, 30000);
    // Same cross-device freshness pattern as CalendarSheet — kick a
    // fresh fetch when the tab/PWA regains focus so the Today/Tomorrow
    // strip catches up immediately after the user reopens FamFeel.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (isDialogOpen()) return;
      loadUpcoming();
      loadMissableLists();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  // Landing on the dashboard (or coming back to the tab) means the user
  // has "seen" whatever pinged them — clear the app-icon badge.
  useEffect(() => {
    clearAppBadge();
    const onVisible = () => {
      if (document.visibilityState === "visible") clearAppBadge();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Handoff from Kitchen Display mode: if a tile was tapped there, sessionStorage
  // carries the member id. Open their check-in dialog as soon as members load.
  // Consumed once (removed after reading) so a page refresh doesn't re-trigger it.
  useEffect(() => {
    let pending;
    try {
      pending = sessionStorage.getItem("ff_pending_checkin");
    } catch {
      pending = null;
    }
    if (!pending || !members.length) return;
    const member = members.find((m) => m.id === pending);
    try { sessionStorage.removeItem("ff_pending_checkin"); } catch {}
    if (member) openCheckin(member);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members.length]);

  // Handle return from Stripe Checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeFlag = params.get("stripe");
    const sessionId = params.get("session_id");
    if (!stripeFlag) return;
    // Clear the query string so refresh doesn't re-trigger.
    window.history.replaceState({}, "", window.location.pathname);
    if (stripeFlag === "cancel") {
      toast("Checkout cancelled", { description: "No charge was made." });
      return;
    }
    if (stripeFlag !== "success" || !sessionId) return;
    // Poll until paid, up to ~20s
    let attempts = 0;
    const maxAttempts = 10;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const s = await api.billingCheckoutStatus(sessionId);
        if (s.payment_status === "paid") {
          toast.success("Welcome to " + (s.tier || "your new plan") + "!", {
            description: "Your plan is active — add your new members now.",
          });
          // Grand or Extended upgrade → flag for the Houses guide
          // toast to fire once the dashboard's tier state catches up.
          // Any device can re-open this guide from Settings later.
          if (s.tier === "extended" || s.tier === "grand") {
            try {
              localStorage.setItem("famfeel_new_houses_v1", "1");
            } catch {}
          }
          onRefresh && onRefresh();
          // Jump straight into the member-adding flow.
          setTimeout(() => {
            // Paying implies this is a parent — skip the PIN prompt once.
            setManagerAutoAdd(true);
            setShowManager(true);
          }, 600);
          return;
        }
        if (s.status === "expired") {
          toast.error("Checkout expired. Try again.");
          return;
        }
      } catch {
        // keep polling on transient errors
      }
      if (attempts < maxAttempts) setTimeout(tick, 2000);
      else toast("Still confirming payment…", { description: "Refresh in a minute." });
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [onRefresh]);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  // Identity picker — replaced with an in-dashboard "big cards" layout
  // when the family has at least one kid under 7. See `youngKidMode`
  // below; the dashboard itself becomes the picker, similar to the
  // kitchen-iPad display mode, so a small child can tap themselves
  // without navigating a modal.
  const youngKidMode = hasYoungKid(members) && !toddlerId && !calendarOnly && !petsOnly;

  const openCheckin = (member) => {
    // House-lock "front door" gate: if the member lives in a locked
    // house and this device hasn't unlocked it yet, show the unlock
    // dialog first; on success we retry the open.
    // EXCEPT when we're on the house-tiles front and the member is a
    // "shared" kid/pet already visible to everyone. Their card is on
    // the front precisely because they opted into cross-house sharing,
    // so a house PIN gate would defeat the point.
    const isSharedFromFront =
      onHouseFront &&
      sharedMembers.some((s) => s.id === member.id);
    if (member.house_id && !isHouseUnlocked(member.house_id) && !isSharedFromFront) {
      const h = (houses || []).find((x) => x.id === member.house_id);
      if (h?.has_pin) {
        setHouseUnlockFor({ house: h, pendingMember: member });
        return;
      }
    }
    if (member.has_pin && member.id !== viewerId) setPinFor(member);
    else {
      // Pets don't become the viewer; only humans do.
      if (member.role !== "pet") setViewerId(member.id);
      setSelectedMember(member);
    }
  };

  const handleReact = async (checkinId, emoji) => {
    if (!viewerId) return;
    const updated = await api.reactCheckin(checkinId, {
      member_id: viewerId,
      emoji,
    });
    setCheckins((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  const handleSeen = async (checkinId, authorId) => {
    if (!viewerId || viewerId === authorId) return;
    try {
      const updated = await api.markSeen(checkinId, { member_id: viewerId });
      setCheckins((prev) =>
        prev.map((c) => (c.id === updated.id ? updated : c))
      );
    } catch {}
  };

  const loggedCount = useMemo(() => {
    const seen = new Set();
    const memberRole = Object.fromEntries(scopedMembers.map((m) => [m.id, m.role]));
    const scopedIds = new Set(scopedMembers.map((m) => m.id));
    for (const c of checkins) {
      if (!scopedIds.has(c.member_id)) continue;
      const isPet = memberRole[c.member_id] === "pet";
      const humanLogged = c.sleep != null || c.mood != null;
      // Pets count as "checked in" only when a REAL care action happened
      // today — a walk, a feed, or fresh water. Scheduling a walk for
      // later (`next_walk_at`) doesn't count, because the pet hasn't
      // actually been walked yet. Mirrors the `petTouched` rule in vibe.js.
      const petLogged = isPet && (
        (c.walks && c.walks.length > 0) ||
        !!c.fed_at ||
        !!c.water_change_at ||
        (c.feeds && c.feeds.length > 0) ||
        (c.water_changes && c.water_changes.length > 0)
      );
      if (humanLogged || petLogged) seen.add(c.member_id);
    }
    return seen.size;
  }, [checkins, scopedMembers]);
  const memberCount = scopedMembers.length;

  // Per-viewer "seen" bookkeeping so we can flag cards whose check-in is newer
  // than what the viewer last looked at.
  const SEEN_KEY = `famfeel_seen_${viewerId || "anon"}`;
  const [seenMap, setSeenMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "{}"); } catch { return {}; }
  });
  useEffect(() => {
    try { setSeenMap(JSON.parse(localStorage.getItem(SEEN_KEY) || "{}")); } catch { setSeenMap({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerId]);

  const markSeenLocal = (memberId, updatedAt) => {
    if (!memberId || !updatedAt) return;
    const next = { ...seenMap, [memberId]: updatedAt };
    setSeenMap(next);
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)); } catch {}
  };

  const isCheckinNew = (memberId) => {
    // Never flag the viewer's own card as "new".
    if (!memberId || memberId === viewerId) return false;
    const c = checkinByMember[memberId];
    if (!c) return false;
    if (c.sleep == null && c.mood == null) return false;
    const last = seenMap[memberId];
    return !last || last < (c.updated_at || c.created_at || "");
  };

  // A member is "shared to the front" when they're a kid or a pet
  // AND their cross-house privacy lets the other house see at least
  // their mood or status. Parents/grandparents live behind their own
  // house door by default — moving a parent onto the front would feel
  // like shouting their mood through the wall. Kids and pets are
  // implicitly the family-wide members the whole extended family
  // wants to see.
  const sharedMembers = useMemo(() => {
    return visibleMembers.filter((m) => {
      if (m.role !== "kid" && m.role !== "pet") return false;
      const p = m.cross_house_privacy || {};
      // Default true means "default shared"; parents can toggle off
      // per-member to pull a kid back behind their house door.
      return p.mood !== false || p.status !== false;
    });
  }, [visibleMembers]);

  // Per-house "inside" counts for the house-tile subtitle. Matches the
  // rule used by the inside view: direct assignees + any shared
  // kids/pets (including unassigned/visible-everywhere ones and those
  // assigned to another house). So the number on the tile === the
  // number of big cards you'll see after tapping in.
  const membersByHouse = useMemo(() => {
    const out = {};
    for (const h of houses || []) {
      const own = visibleMembers.filter((m) => m.house_id === h.id);
      const shared = visibleMembers.filter((m) => {
        if (m.role !== "kid" && m.role !== "pet") return false;
        if ((m.house_id || "") === h.id) return false; // already in `own`
        const p = m.cross_house_privacy || {};
        return p.mood !== false || p.status !== false;
      });
      out[h.id] = own.length + shared.length;
    }
    return out;
  }, [houses, visibleMembers]);

  // Active house resets if the house was deleted or the family
  // downgraded. Defensive cleanup so we never dangle a stale id. Only
  // runs after the initial houses fetch completes — otherwise we'd
  // clear the active house on every mount while the list is still
  // empty, bouncing the user away from their current house.
  useEffect(() => {
    if (!activeHouseId || !housesLoaded) return;
    const stillExists = (houses || []).some((h) => h.id === activeHouseId);
    if (!stillExists) setActiveHouseId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [houses, activeHouseId, housesLoaded]);

  // Tapping a house tile from the front. Three paths: (1) no PIN yet
  // → force the first parent to set one before entering, (2) locked
  // on this device → ask for the PIN and step inside on success, (3)
  // already unlocked → walk straight in.
  const enterHouse = (h) => {
    if (!h.has_pin) {
      setHouseFirstPinFor(h);
      return;
    }
    if (!isHouseUnlocked(h.id)) {
      setHouseUnlockFor({ house: h, pendingMember: null, afterUnlock: "enter" });
      return;
    }
    setActiveHouseId(h.id);
  };

  // True when the family has houses and we're on the "which house?"
  // front screen. Drives the big switch between house-tile front and
  // the normal per-house member grid.
  const onHouseFront =
    (houses?.length || 0) > 0 && !activeHouseId;

  // Big tile layout: default dashboard. Each tile carries the person's
  // current mood tint (green/yellow/orange/red) so the whole family's
  // "how are we today" reads at a glance. Small text under the name
  // switches per role:
  //   - Humans: mood emoji + "MOOD N", or "NOT YET" if nothing logged.
  //   - Pets: "1 WALK TODAY" / "2 WALKS TODAY" (walks-based families know
  //     the dog's status without entering its card).
  //
  // Used for every family except the teen/adult-only "compact" mode,
  // which keeps the smaller MemberCard layout.
  // Precompute per-member "away right now" info from the upcoming
  // events list. A member is "away" if they're tagged on a
  // multi-day travel/trip event whose start is in the past and
  // end is in the future. Silent when nobody is away.
  const tripByMember = useMemo(() => {
    const now = new Date();
    const map = new Map();
    for (const ev of upcoming || []) {
      const kind = ev.kind || "other";
      if (kind !== "travel" && kind !== "day_trip") continue;
      if (!ev.starts_at || !ev.ends_at) continue;
      const start = new Date(ev.starts_at);
      const end = new Date(ev.ends_at);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;
      if (start > now || end < now) continue; // not currently active
      const dayDiff = Math.round((end - now) / (1000 * 60 * 60 * 24));
      let backLabel;
      if (dayDiff <= 0) backLabel = "today";
      else if (dayDiff === 1) backLabel = "tomorrow";
      else backLabel = `in ${dayDiff} days`;
      for (const mid of ev.member_ids || []) {
        // Nearest-end-first wins if multiple trips somehow overlap.
        const prev = map.get(mid);
        if (!prev || end < prev.endDate) {
          map.set(mid, {
            title: ev.title || "",
            backLabel,
            endDate: end,
          });
        }
      }
    }
    return map;
  }, [upcoming]);

  const renderBigTile = (m, i) => {
    const lockedOut = isToddlerMode && m.id !== toddlerId;
    const checkin = checkinByMember[m.id];
    const walkLocked = m.role === "pet" && isPetWalkLockedForPet(m, members, tier);
    return (
      <BigTile
        key={m.id}
        member={m}
        index={i}
        lockedOut={lockedOut}
        checkin={checkin}
        compactLayout={compactLayout}
        todoStatus={todosByMember[m.id]}
        tripInfo={tripByMember.get(m.id) || null}
        onOpenTodos={() => setShowTodoLists(true)}
        onOpen={() => !lockedOut && openCheckin(m)}
        onPeek={(mem, rect) => {
          if (lockedOut) return;
          setPeekFor({ member: mem, rect });
        }}
        walkLocked={walkLocked}
        onWalkUpgrade={() => {
          setBilling({ open: true, reason: "pet_care" });
        }}
        onLogWalkNow={async (pet) => {
          if (!pet?.id) return;
          // Logged-by attribution: use the currently-locked-in viewer
          // when one exists, otherwise fall back to a "Family" label
          // so the request still satisfies the backend's "pick a
          // walker or type a name" guard. Backend field is
          // `walked_by` (NOT `by`) — the previous payload silently
          // failed validation and surfaced as the generic
          // "Couldn't log walk — try again?" toast.
          try {
            const payload = { pet_id: pet.id };
            if (viewerId) {
              payload.walked_by = viewerId;
            } else {
              payload.walked_by_label = "Family";
            }
            await api.logPetWalk(payload);
            toast.success(`Walk logged for ${pet.name}`);
            // Refetch today's checkins so the tile flips to "1 walk
            // today" instantly without waiting for the next poll.
            try {
              const cs = await api.todayCheckins({});
              if (Array.isArray(cs)) setCheckins(cs);
            } catch {
              /* non-fatal — next poll will catch up */
            }
          } catch (e) {
            const detail = e?.response?.data?.detail || "";
            if (e?.response?.status === 403 && /upgrade/i.test(detail)) {
              toast.error(detail, {
                action: {
                  label: "Upgrade",
                  onClick: () => setBilling({ open: true, reason: "pet_care" }),
                },
              });
            } else {
              toast.error("Couldn't log walk — try again?");
            }
          }
        }}
      />
    );
  };

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      {/* Header — minimal, editorial. Sticky "following menu" so quick
          actions (calendar, chores, lists, settings) stay reachable when
          the family scrolls down through pets/events/happening-now.

          Android-Chrome guard: some Adreno/Mali GPU drivers blank the
          whole viewport when `position: sticky` meets `backdrop-filter:
          blur(...)`. We keep the paper-white background solid (no
          backdrop-blur) so the header stays legible over any scrolled-up
          content without tripping the compositor. */}
      <header className="sticky top-0 z-40 px-4 md:px-10 pt-4 md:pt-6 pb-4 md:pb-5 bg-[#F9F9F8] border-b border-[color:var(--line-soft)]">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-display text-xl md:text-2xl">FamFeel</span>
            <span className="label-tiny">
              {settings?.family_name || "Family"} · {today}
            </span>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 flex-wrap justify-end">
            {isToddlerMode ? (
              <button
                onClick={() => setShowUnlock(true)}
                className="ff-btn ff-btn-icon opacity-60 hover:opacity-100"
                data-testid="toddler-unlock-btn"
                title="Unlock toddler mode"
                aria-label="Unlock toddler mode"
              >
                <Lock className="w-3.5 h-3.5" strokeWidth={2} />
              </button>
            ) : (
              <>
            {streak && streak.streak > 0 && !calendarOnly && !petsOnly && (
              <div
                className="ff-chip tabular"
                data-testid="family-streak"
                title={`All ${streak.members} members checked in · ${streak.streak}-day streak`}
              >
                <Flame className="w-3.5 h-3.5" strokeWidth={2} />
                <span className="hidden sm:inline">{streak.streak}-day streak</span>
                <span className="sm:hidden">{streak.streak}d</span>
              </div>
            )}
            {viewer && (
              <div
                className="ff-chip tabular pr-1 py-1 text-[11px] gap-1"
                data-testid="viewer-chip"
                title={`${viewer.name} — last person who tapped a card`}
              >
                <span className="max-w-[96px] truncate uppercase tracking-wider">
                  {viewer.name}
                </span>
                <button
                  onClick={() => setViewerId(null)}
                  className="ml-0.5 w-4 h-4 rounded-full inline-flex items-center justify-center hover:bg-[color:var(--bg-soft)] text-[color:var(--ink-3)] hover:text-[color:var(--ink)] transition-colors"
                  title="Not me"
                  aria-label="Clear current viewer"
                  data-testid="viewer-chip-clear"
                >
                  <X className="w-2.5 h-2.5" strokeWidth={2.5} />
                </button>
              </div>
            )}
            {/* "Who's free?" — works in every dashboard mode (full,
                calendar-only, pets-only). Calendar-only families need
                this feature *more*, not less, since their dashboard
                is the calendar. Only requirement: at least 2 humans
                in the family, and not in toddler/single-user lock. */}
            {!isToddlerMode && members.filter((m) => m.role !== "pet").length >= 2 && (
              <button
                onClick={() => setShowWhosFree(true)}
                className="ff-btn ff-btn-icon"
                data-testid="whos-free-btn"
                title={t({ en: "Who's available?", da: "Hvem er ledig?" })}
                aria-label={t({ en: "Who's available?", da: "Hvem er ledig?" })}
              >
                <HelpCircle className="w-4 h-4" strokeWidth={2} />
              </button>
            )}
            {!calendarOnly && (
              <button
                onClick={() => openCalendarAndMarkSeen()}
                className="ff-btn ff-btn-icon relative"
                data-testid="open-calendar-btn"
                title={calendarHasUnseen ? "Family calendar — something new since you last looked" : "Family calendar"}
                aria-label="Family calendar"
              >
                <CalendarIcon className="w-4 h-4" strokeWidth={2} />
                {calendarHasUnseen && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[color:var(--ink)] ring-2 ring-[color:var(--surface)]"
                    aria-hidden="true"
                    data-testid="calendar-unseen-dot"
                  />
                )}
              </button>
            )}
            <button
              onClick={() => setShowChores(true)}
              className="ff-btn ff-btn-icon"
              data-testid="open-chores-btn"
              title={t({ en: "Chores", da: "Pligter" })}
              aria-label={t({ en: "Chores", da: "Pligter" })}
            >
              <ListChecks className="w-4 h-4" strokeWidth={2} />
            </button>
            <button
              onClick={() => setShowTodoLists(true)}
              className="ff-btn ff-btn-icon"
              data-testid="open-todos-btn"
              title={t({ en: "Lists", da: "Lister" })}
              aria-label={t({ en: "Lists", da: "Lister" })}
            >
              <ListTodo className="w-4 h-4" strokeWidth={2} />
            </button>
            {members.some((m) => m.role === "pet") && (
              <button
                onClick={() => navigate("/app/pets")}
                className="ff-btn ff-btn-icon"
                data-testid="open-pets-btn"
                title={t({ en: "Pets overview", da: "Kæledyr-overblik" })}
                aria-label={t({ en: "Pets", da: "Kæledyr" })}
              >
                <PawPrint className="w-4 h-4" strokeWidth={2} />
              </button>
            )}
            {!onHouseFront && (
              <button
                onClick={() => navigate("/display")}
                className="ff-btn ff-btn-icon"
                data-testid="open-display-btn"
                title={t({ en: "Ambient display (for a kitchen iPad)", da: "Ambient-display (til en iPad i køkkenet)" })}
                aria-label={t({ en: "Ambient display", da: "Ambient-display" })}
              >
                <Tv className="w-4 h-4" strokeWidth={2} />
              </button>
            )}
            {hasToddler(members) && (
              <button
                onClick={() => {
                  // One-tap lock: pick the youngest kid under 7 (matches
                  // `hasToddler` which gates button visibility). Earlier
                  // code used `< 5` here, which meant a family with only
                  // 5- or 6-year-old kids saw the button but tapping did
                  // nothing. Any kid who qualifies for the big-tile /
                  // young-kid UI qualifies for the one-tap lock too.
                  const toddlers = members
                    .filter((m) => {
                      if (!m || m.role !== "kid") return false;
                      const age = ageFromBirthday(m.birthday);
                      return age != null && age < 7;
                    })
                    .sort((a, b) => {
                      // Youngest first = largest birthday (most recent date).
                      const ba = a.birthday || "";
                      const bb = b.birthday || "";
                      return bb.localeCompare(ba);
                    });
                  if (toddlers.length === 0) return;
                  const pick = toddlers[0];
                  setToddlerMemberId(pick.id);
                  setToddlerId(pick.id);
                  toast.success(
                    toddlers.length > 1
                      ? `Toddler mode on for ${pick.name} · change in Settings`
                      : `Toddler mode on for ${pick.name}`
                  );
                }}
                className="ff-btn ff-btn-icon"
                data-testid="open-toddler-btn"
                title={t({ en: "Toddler mode", da: "Småbørns-mode" })}
                aria-label={t({ en: "Toddler mode", da: "Småbørns-mode" })}
              >
                <Smile className="w-4 h-4" strokeWidth={2} />
              </button>
            )}
            <button
              onClick={() => requireFamilyPin(() => setShowManager(true))}
              className="ff-btn ff-btn-icon"
              data-testid="open-manager-btn"
              title={t({ en: "Settings & family", da: "Indstillinger & familie" })}
              aria-label={t({ en: "Settings", da: "Indstillinger" })}
            >
              <Settings className="w-4 h-4" strokeWidth={2} />
            </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Top-of-page nudges — push permission, billing past-due, etc.
          Mounted right under the header so they're visible regardless
          of dashboard_mode (calendar-only families would otherwise
          never see them — the hero section is hidden, member grid
          sits well below the fold). Order matters: push permission
          first (most important — without it nothing else lands),
          billing failures second (account integrity), trial nudge
          last (revenue but never blocks the user). */}
      <section className="px-6 md:px-10 pb-2" data-testid="dashboard-top-nudges">
        <div className="max-w-6xl mx-auto">
          {!isToddlerMode && (
            <PushRevokedBanner
              settings={settings}
              viewerId={viewerId}
              onRefresh={onRefresh}
            />
          )}
          {!isToddlerMode && (
            <NotificationsOffBanner
              settings={settings}
              viewerId={viewerId}
              onRefresh={onRefresh}
            />
          )}
          {!isToddlerMode && calendarOnly && (
            <>
              <BillingPastDueBanner />
              <TrialNudgeBanner
                onTryPlus={() => setBilling({ open: true, reason: "trial_nudge" })}
              />
            </>
          )}
        </div>
      </section>

      {/* Hero phrase (hidden in calendar-only mode) */}
      <section className="px-6 md:px-10 pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto">
          <BillingPastDueBanner />
          <TrialNudgeBanner onTryPlus={() => setBilling({ open: true, reason: "trial_nudge" })} />
          {/* Birthday banner — celebratory ribbon for any family member
              with a birthday today or tomorrow. Skipped in toddler
              mode (toddlers get their own countdown screen on tap).
              Tapping it fires a confetti burst via the existing
              window-level famfeel:celebrate event so it feels alive. */}
          {/* BirthdayBanner removed — the TodayPulse headline already
              reads "Tomorrow is HC's birthday 🎂" and the event row
              below shows it too, so the banner was visual duplication.
              Confetti burst is still triggered from the calendar
              birthday row's onClick. */}
          {/* Week-in-review summary — shows at the top of the dashboard
              on mood, calendar and pet modes (any mode where the family
              is actually viewing their dashboard, vs the houses-front
              picker or the toddler mode). One consistent anchor point
              regardless of which dashboard layout is active. */}
          {!isToddlerMode && !onHouseFront && <WeekInReviewCard />}
          {!calendarOnly && !petsOnly && !onHouseFront && (
            <>
              <h1
                className="font-display-italic text-3xl md:text-5xl lg:text-6xl leading-[0.98] rise-in tracking-tight"
                data-testid="at-a-glance"
              >
                {phrase}
              </h1>
              <div className="mt-8 label-tiny" data-testid="logged-count">
                {t({
                  en: `${loggedCount} of ${memberCount} checked in today`,
                  da: `${loggedCount} af ${memberCount} har tjekket ind i dag`,
                })}
              </div>
              <div className="label-tiny text-[color:var(--ink-3)] mt-1">
                {t({
                  en: "Log in as many times as you like",
                  da: "Log ind lige så mange gange du vil",
                })}
              </div>
            </>
          )}
          {/* Toddler picker rises above the greeting — the squares are
              the most attention-grabbing thing on screen for little
              ones, and burying them under a 13:00 prompt or event list
              made the page read backwards in toddler mode. */}
          {isToddlerMode && calendarOnly && (
            <div className="mb-6">{toddlerFunPicker}</div>
          )}
          {onHouseFront && null}
          {calendarOnly && !onHouseFront && (
            <h1
              className="font-display-italic text-3xl md:text-5xl lg:text-6xl leading-[0.98] rise-in tracking-tight"
              data-testid="at-a-glance-calendar"
            >
              {/* Use the same time-of-day greeting pool as the
                  DisplayMode hero so the front-of-house and the
                  in-app calendar feel like one experience. The
                  pool is deterministic per (day-of-year × band) —
                  no flicker as minutes tick. The structural
                  "What's coming up" was leaving the H1 hollow
                  once TodayPulse went silent on 2+ event days.
                  When the user has nothing left on today, swap to
                  the calm "empty pool" so H1 doesn't say "Today
                  continues" while TodayPulse below says "Nothing
                  else on. Pour a slow drink." */}
              {(() => {
                const todayKey = (() => {
                  const d = new Date();
                  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                })();
                const nowMs = Date.now();
                const eventsLeftToday = (upcoming || []).filter((e) => {
                  if (!e.starts_at) return false;
                  const sd = new Date(e.starts_at);
                  if (Number.isNaN(sd.getTime())) return false;
                  const k = `${sd.getFullYear()}-${String(sd.getMonth() + 1).padStart(2, "0")}-${String(sd.getDate()).padStart(2, "0")}`;
                  if (k !== todayKey) return false;
                  // Drop events that have already finished — same logic
                  // TodayPulse uses to decide "still ahead today".
                  if (e.ends_at) {
                    const ed = new Date(e.ends_at);
                    if (!Number.isNaN(ed.getTime()) && ed.getTime() < nowMs) return false;
                  }
                  return true;
                }).length;
                return greetingFor(new Date(), {
                  calendarOnly: true,
                  eventsLeftToday,
                  lang,
                });
              })()}
            </h1>
          )}
          {petsOnly && (
            <h1
              className="font-display-italic text-3xl md:text-5xl lg:text-6xl leading-[0.98] rise-in tracking-tight"
              data-testid="at-a-glance-pets"
            >
              {visibleMembers.length === 0
                ? "No pets yet"
                : visibleMembers.length === 1
                  ? "Your pet today"
                  : `Your ${visibleMembers.length} pets today`}
            </h1>
          )}
          {calendarOnly && !petsOnly && !isToddlerMode && !onHouseFront && false && (
            <div
              className="mt-4 flex flex-wrap items-center gap-2"
              data-testid="calendar-only-filters"
            >
              <select
                value={coMemberFilter}
                onChange={(e) => setCoMemberFilter(e.target.value)}
                className="ff-input py-1.5 text-[13px] w-auto min-w-[140px]"
                data-testid="calendar-only-filter-member"
                aria-label="Filter by who"
              >
                <option value="all">{t({ en: "Everyone", da: "Alle" })}</option>
                {viewerId && members.some((m) => m.id === viewerId) && (
                  <option value={viewerId}>Just me</option>
                )}
                {visibleMembers.some((m) => m.role === "pet") && (
                  <option value="__pets__">🐾 Pets only</option>
                )}
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.avatar_emoji} {m.name}
                  </option>
                ))}
              </select>
              <select
                value={coKindFilter}
                onChange={(e) => setCoKindFilter(e.target.value)}
                className="ff-input py-1.5 text-[13px] w-auto min-w-[140px]"
                data-testid="calendar-only-filter-kind"
                aria-label="Filter by kind"
                disabled={coMemberFilter === "__pets__"}
                style={coMemberFilter === "__pets__" ? { opacity: 0.4 } : undefined}
              >
                <option value="all">All kinds</option>
                {EVENT_KINDS.filter((k) => k.key !== "kids" || !isTeenPlusFamily(members)).map((k) => (
                  <option key={k.key} value={k.key}>
                    {k.emoji} {k.label}
                  </option>
                ))}
              </select>
              {(coMemberFilter !== "all" || coKindFilter !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setCoMemberFilter("all");
                    setCoKindFilter("all");
                  }}
                  className="label-tiny text-[color:var(--ink-3)] hover:text-[color:var(--ink)] underline"
                  data-testid="calendar-only-clear-filters"
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setCalendarAutoAdd(true);
                  setShowCalendar(true);
                }}
                className="ff-btn ff-btn-primary ml-auto py-1.5 text-[13px] inline-flex items-center gap-1"
                data-testid="calendar-only-add-event"
              >
                <CalendarIcon className="w-3.5 h-3.5" strokeWidth={2} />
                Add event
              </button>
            </div>
          )}
          {/* Removed — toddler picker now rises above the greeting,
              not after the filters. */}
          {/* "Don't forget" lists — matches the CalendarSheet
              EventRow shape (full-width rounded-xl row, px-3 py-2.5,
              36x36 emoji on the left, soft tint background) so on a
              calendar-mode dashboard the strip reads as a natural
              extension of the day-grouped events list rather than a
              different component. Rendered here, just above the main
              calendar/events block, so in calendar-only mode (where
              the CalendarSheet fills the viewport) the list actually
              appears at the top of the page instead of buried far
              below. Hidden in toddler mode. */}
          {!isToddlerMode && missableLists.length > 0 && (
            <div
              className="mt-6 mb-4 px-5 md:px-7"
              data-testid="dashboard-missable-lists"
            >
              <div className="label-tiny mb-1.5 text-[color:var(--ink-2)]">
                {t({ en: "Don't forget", da: "Husk" })}
              </div>
              <ul className="space-y-1.5">
                {missableLists.map(({ list, event, when, reason }) => {
                  const done = Number(list.done_count || 0);
                  const total = Number(list.item_count || 0);
                  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                  const now = new Date();
                  const isOverdue = when < now;
                  const daysAway = Math.max(
                    0,
                    Math.round((when - now) / (1000 * 60 * 60 * 24)),
                  );
                  const whenLabel = isOverdue
                    ? t({ en: "Overdue", da: "Forfalden" })
                    : daysAway === 0
                      ? t({ en: "Today", da: "I dag" })
                      : daysAway === 1
                        ? t({ en: "Tomorrow", da: "I morgen" })
                        : t({ en: `In ${daysAway} days`, da: `Om ${daysAway} dage` });
                  const contextLine = reason === "event" && event
                    ? `${event.title} · ${whenLabel.toLowerCase()}`
                    : total > 0
                      ? t({ en: `${done} of ${total} done`, da: `${done} af ${total} klaret` })
                      : t({ en: "Nothing added yet", da: "Ingen ting tilføjet endnu" });
                  const taggedMembers = (list.member_ids || [])
                    .map((id) => members.find((m) => m.id === id))
                    .filter(Boolean);
                  // Match the calendar month-grid surface — pure
                  // white with a 1px ink keyline. Keeps the box
                  // visually quiet and consistent with the rest of
                  // the dashboard's panel system.
                  const listTint = "var(--surface)";
                  const listBorder = "rgba(11,11,9,0.10)";
                  return (
                    <li
                      key={list.id}
                      className="relative px-3 py-2 rounded-xl flex items-start gap-3 cursor-pointer select-none hover:brightness-[0.98]"
                      style={{ background: listTint, border: `1px solid ${listBorder}` }}
                      onClick={() => {
                        setTodoListsInitialId(list.id);
                        setShowTodoLists(true);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setTodoListsInitialId(list.id);
                          setShowTodoLists(true);
                        }
                      }}
                      data-testid={`missable-list-${list.id}`}
                      aria-label={`Open the list ${list.title}, ${contextLine}`}
                    >
                      <span
                        className="w-8 h-8 flex items-center justify-center text-xl flex-shrink-0"
                        aria-hidden="true"
                      >
                        📝
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-[15px] leading-tight tracking-tight truncate">
                          {list.title}
                          {isOverdue && (
                            <span
                              className="ml-1.5 align-middle inline-block text-[11px] uppercase tracking-[0.06em] text-[color:var(--rough-strong,#b54708)]"
                            >
                              · overdue
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.06em] mt-0.5 text-[color:var(--ink-3)] truncate">
                          {whenLabel}
                          <span className="mx-1 opacity-60">·</span>
                          <span className="normal-case tracking-normal">{contextLine}</span>
                        </div>
                      </div>
                      {taggedMembers.length > 0 && (
                        // Tagged members rail moves to the top-right
                        // of the card so the box can stay compact
                        // (no extra row). Sizes match the event-pill
                        // avatar rail elsewhere on the dashboard
                        // (w-6 h-6) — bigger felt out of place once
                        // the card height was tightened.
                        <div className="flex -space-x-1 flex-shrink-0 self-start mt-0.5">
                          {taggedMembers.slice(0, 6).map((m) =>
                            m.avatar_image ? (
                              <img
                                key={m.id}
                                src={m.avatar_image}
                                alt={m.name}
                                title={m.name}
                                className="w-6 h-6 rounded-full object-cover border border-[color:var(--surface)]"
                              />
                            ) : (
                              <span
                                key={m.id}
                                className="w-6 h-6 text-sm rounded-full bg-[color:var(--surface)] border border-[color:var(--bg)] flex items-center justify-center"
                                title={m.name}
                              >
                                {m.avatar_emoji}
                              </span>
                            ),
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {/* Chores card — top 2 due today + 2 most-recently-done.
              Card is always white; "late" badges are the only red.
              Mirrors the "Don't forget" rail recipe so the two stack
              as one quiet "things to act on" column. */}
          {!isToddlerMode && upcomingChores.length > 0 && (
            <div
              className="mt-3 mb-4 px-5 md:px-7"
              data-testid="dashboard-upcoming-chores"
            >
              <div className="label-tiny mb-1.5 text-[color:var(--ink-2)]">
                {t({ en: "Chores", da: "Pligter" })}
              </div>
              <ul className="space-y-1.5">
                {upcomingChores.map((c) => {
                  const assignee = members.find((m) => m.id === c.assignee_id);
                  const timeLabel = c.due_time || t({ en: "All day", da: "Hele dagen" });
                  // "How late are we?" — computed client-side from
                  // due_time + current local clock so the indicator
                  // refreshes when the user reopens the dashboard,
                  // even if `c.overdue_today` was already true on
                  // the last poll. Falls back to the backend's
                  // pre-computed boolean for the all-day case where
                  // no minute math applies. Skipped when the chore
                  // is already done (the completed row visually
                  // wins; lateness is moot).
                  let overdueText = null;
                  if (!c.completed_today && c.overdue_today && c.due_time) {
                    try {
                      const [h, m] = c.due_time.split(":").map(Number);
                      const now = new Date();
                      const due = new Date(now);
                      due.setHours(h, m, 0, 0);
                      const minsLate = Math.max(0, Math.round((now - due) / 60000));
                      if (minsLate < 60) {
                        overdueText = t({ en: `${minsLate}m late`, da: `${minsLate}m for sen` });
                      } else if (minsLate < 24 * 60) {
                        const hrs = Math.floor(minsLate / 60);
                        const rem = minsLate % 60;
                        overdueText = rem
                          ? t({ en: `${hrs}h ${rem}m late`, da: `${hrs}t ${rem}m for sen` })
                          : t({ en: `${hrs}h late`, da: `${hrs}t for sen` });
                      } else {
                        overdueText = t({ en: "Overdue", da: "Forfalden" });
                      }
                    } catch {
                      overdueText = t({ en: "Overdue", da: "Forfalden" });
                    }
                  } else if (!c.completed_today && c.overdue_today) {
                    overdueText = t({ en: "Overdue", da: "Forfalden" });
                  }
                  // Dusty terracotta — sits on the pastel spectrum
                  // (next to pink/peach) instead of the old warm-rust
                  // `--rough-strong` which read as bright orange.
                  // Used for the "late" badge only; the card stays white.
                  const LATE_BG = "#C26763";
                  return (
                    <li
                      key={c.id}
                      className={`relative px-3 py-2 rounded-xl flex items-start gap-3 cursor-pointer select-none hover:brightness-[0.98] ${
                        c.completed_today ? "opacity-60" : ""
                      }`}
                      style={{
                        background: "var(--surface)",
                        border: "1px solid rgba(11,11,9,0.10)",
                      }}
                      onClick={() => setShowChores(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setShowChores(true);
                        }
                      }}
                      data-testid={`upcoming-chore-${c.id}`}
                      aria-label={`Open chores. ${c.title} at ${timeLabel}${overdueText ? `, ${overdueText}` : c.completed_today ? ", done" : ""}`}
                    >
                      <span
                        className="w-8 h-8 flex items-center justify-center text-xl flex-shrink-0"
                        aria-hidden="true"
                      >
                        {c.completed_today ? "✔️" : "✅"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-display text-[15px] leading-tight tracking-tight truncate ${
                            c.completed_today ? "line-through text-[color:var(--ink-2)]" : ""
                          }`}
                        >
                          {c.title}
                          {overdueText && (
                            <span
                              className="ff-accent-hot ml-1.5 align-middle inline-block px-1.5 py-[1px] rounded-md text-[10px] uppercase tracking-[0.08em] font-semibold"
                              data-testid={`upcoming-chore-late-${c.id}`}
                            >
                              {overdueText}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.06em] mt-0.5 text-[color:var(--ink-3)] truncate">
                          {timeLabel}
                          {assignee?.name && (
                            <>
                              <span className="mx-1 opacity-60">·</span>
                              <span className="normal-case tracking-normal">
                                {assignee.name}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {assignee && (
                        <div className="flex-shrink-0 self-start mt-0.5">
                          {assignee.avatar_image ? (
                            <img
                              src={assignee.avatar_image}
                              alt={assignee.name}
                              title={assignee.name}
                              className="w-6 h-6 rounded-full object-cover border border-[color:var(--surface)]"
                            />
                          ) : (
                            <span
                              className="w-6 h-6 text-sm rounded-full bg-[color:var(--surface)] border border-[color:var(--bg)] flex items-center justify-center"
                              title={assignee.name}
                            >
                              {assignee.avatar_emoji}
                            </span>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {(() => {
            // Calendar-mode dashboard: render the unified CalendarSheet
            // inline so the page IS the calendar (no popup needed).
            // Toddler calendar-front and houses-front keep the original
            // strip layout. Pets-only mode also opts out — its dashboard
            // is the pet cards, not events.
            if (
              calendarOnly &&
              !isToddlerMode &&
              !onHouseFront &&
              !petsOnly
            ) {
              return (
                <CalendarSheet
                  inline
                  members={members}
                  viewerId={viewerId}
                  autoOpenAdd={false}
                  scrollToEventId={calendarScrollTo}
                  openEditEventId={calendarOpenEdit}
                  hasBYOKey={!!settings?.byo_google_ai_key_set}
                  onOpenSettings={() => {
                    setShowManager(true);
                    // MembersManager defaults to the "Members" tab; the
                    // BYO Google AI key control lives under "Family", so
                    // jump straight there. Use a microtask delay so the
                    // event listener inside the manager is mounted by
                    // the time we dispatch.
                    setTimeout(() => {
                      window.dispatchEvent(
                        new CustomEvent("famfeel:open-manager-tab", {
                          detail: { tab: "family" },
                        }),
                      );
                    }, 0);
                  }}
                  requestDeleteEventId={calendarRequestDelete}
                  onClose={() => {}}
                />
              );
            }
            // Toddler mode on the full dashboard stays event-free (we surface the
            // fun picker instead). But on the calendar-only front we DO want the
            // toddler to see their own upcoming events — just filtered to items
            // they're attending and with all edit controls stripped.
            if (isToddlerMode && !calendarOnly) return null;
            // Pets-only dashboard hides the entire upcoming events strip.
            // The pets-only screen IS the pet care cards — calendar
            // events for the dog (vet, groomer) are still reachable
            // from the menu / calendar mode, but cluttering the pet
            // dashboard with them undercut the clarity of the dog's
            // walk-and-feed status. The user explicitly asked for the
            // calendar to disappear here.
            if (petsOnly) return null;
            let merged = mergeWithBirthdays(upcoming, members);
            // Houses "front door" view: the events strip above the house
            // tiles is a shared billboard, so show only events that are
            // family-wide (no house scope). Birthdays are always shared
            // so keep those even though the synthesized event has no
            // house_id either way.
            if (onHouseFront) {
              merged = merged.filter(
                (ev) => !ev.house_id || ev.source === "birthday",
              );
            }
            if (isToddlerMode && calendarOnly) {
              merged = merged.filter((ev) =>
                (ev.member_ids || []).includes(toddlerId)
              );
            } else if (calendarOnly) {
              // "Pets only" short-circuits the grid — we empty the event
              // list so the pet cards below float up into the viewport.
              if (coMemberFilter === "__pets__") {
                merged = [];
              } else {
                // Apply inline Who/Kind filters (mirrors CalendarSheet).
                if (coMemberFilter !== "all") {
                  merged = merged.filter((ev) =>
                    (ev.member_ids || []).includes(coMemberFilter)
                  );
                }
                if (coKindFilter !== "all") {
                  merged = merged.filter(
                    (ev) => (ev.kind || "other") === coKindFilter
                  );
                }
              }
            }
            // Calendar-only front view: show ~30 cards on phone (phone
            // grid is 2 cols so 30 ≈ 15 rows, easy thumb-scroll),
            // and up to 90 on iPad/desktop so the viewport fills rather
            // than leaving a sad sparse grid at the top of a big screen.
            // Pets-only mode mirrors mood-mode's "horizontal strip" but
            // bumps the target to ~12 so the rail is more than just
            // 1-2 lonely birthday cards. Mood mode itself sticks at 8.
            const stripEvents = selectStripEvents(merged, {
              targetMin: calendarOnly ? 30 : petsOnly ? 12 : 8,
              hardMax: calendarOnly ? 90 : petsOnly ? 30 : 20,
            });
            if (stripEvents.length === 0) return null;
            const dayKey = (iso) => {
              const d = new Date(iso);
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            };
            // Houses front screen: show a compact horizontal strip of
            // small pills (Display Mode "Coming up" style) — the full
            // house tiles occupy the rest of the viewport, so the event
            // row needs to stay in its lane.
            if (onHouseFront) {
              return (
                <div
                  className="mt-5 flex gap-3 overflow-x-auto no-scrollbar -mx-6 md:-mx-10 px-6 md:px-10"
                  data-testid="upcoming-strip"
                >
                  {stripEvents.slice(0, 8).map((ev) => {
                    const k = dayKey(ev.starts_at);
                    return (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={() => setShowCalendar(true)}
                        className="flex-shrink-0 rounded-xl border border-[color:var(--line)] px-4 py-2.5 bg-[color:var(--bg-soft)] hover:bg-[color:var(--surface)] transition-colors text-left"
                        data-testid={`upcoming-pill-${ev.id}`}
                      >
                        <div className="label-tiny text-[color:var(--ink-3)]">
                          {dayLabel(k, lang)}
                        </div>
                        <div className="font-display text-base leading-tight tracking-tight mt-0.5 whitespace-nowrap">
                          {ev.title}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            }
            let lastKey = null;
            return (
              <div
                className={
                  calendarOnly
                    ? "mt-5 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 auto-rows-fr gap-3 md:gap-4 items-stretch [&>button]:!min-w-0 [&>button]:!max-w-none [&>button]:w-full [&>button]:h-full"
                    : "mt-5 flex gap-3 overflow-x-auto -mx-6 md:-mx-10 px-6 md:px-10 items-start scroll-smooth"
                }
                data-testid="upcoming-strip"
              >
                {stripEvents.map((ev) => {
                  const k = dayKey(ev.starts_at);
                  const showDivider = !calendarOnly && lastKey !== null && k !== lastKey;
                  lastKey = k;
                  // Toddler-only exception to the no-click rule: when
                  // the event is the pinned toddler's own birthday AND
                  // the big day is within 7 days, tapping opens a
                  // playful countdown screen instead of the calendar.
                  // `starts_at` on birthday events is midnight local, so
                  // comparing at day-granularity is safe.
                  let isToddlerOwnBday = false;
                  let toddlerBdayDaysUntil = null;
                  let toddlerBdayAge = null;
                  if (
                    isToddlerMode &&
                    ev.source === "birthday" &&
                    (ev.member_ids || []).includes(toddlerId)
                  ) {
                    const now = new Date();
                    const today0 = new Date(
                      now.getFullYear(),
                      now.getMonth(),
                      now.getDate()
                    );
                    const evDate = new Date(ev.starts_at);
                    const ev0 = new Date(
                      evDate.getFullYear(),
                      evDate.getMonth(),
                      evDate.getDate()
                    );
                    const diff = Math.round(
                      (ev0 - today0) / (24 * 60 * 60 * 1000)
                    );
                    // Within 3 days triggers the countdown treatment.
                    // Narrower than 7 so it only fires in the genuinely
                    // "soon" window — a week-out card reads as noise to
                    // a toddler, 3 days is where the anticipation kicks in.
                    if (diff >= 0 && diff <= 3) {
                      isToddlerOwnBday = true;
                      toddlerBdayDaysUntil = diff;
                      // "turns N" lives at the tail of the title
                      const m = /turns\s+(\d+)/i.exec(ev.title || "");
                      if (m) toddlerBdayAge = Number(m[1]);
                    }
                  }
                  const toddlerMember = toddlerId
                    ? members.find((x) => x.id === toddlerId) || null
                    : null;
                  return (
                    <React.Fragment key={ev.id}>
                      {showDivider && (
                        <div
                          className="flex flex-col items-center justify-center flex-shrink-0 px-1"
                          data-testid={`strip-divider-${k}`}
                          aria-hidden="true"
                        >
                          <div className="w-px flex-1 bg-[color:var(--ink)]/15" />
                          <div
                            className="label-tiny text-[color:var(--ink-2)] py-2 whitespace-nowrap"
                            style={{
                              writingMode: "vertical-rl",
                              transform: "rotate(180deg)",
                            }}
                          >
                            {dayLabel(k, lang)}
                          </div>
                          <div className="w-px flex-1 bg-[color:var(--ink)]/15" />
                        </div>
                      )}
                      <EventPill
                        event={ev}
                        members={members}
                        viewerId={viewerId}
                        onClick={() => {
                          if (isToddlerOwnBday) {
                            setBdayCountdown({
                              member: toddlerMember,
                              age: toddlerBdayAge,
                              daysUntil: toddlerBdayDaysUntil,
                            });
                            return;
                          }
                          setCalendarScrollTo(ev.id);
                          setShowCalendar(true);
                        }}
                        onOpenLinkedList={(listId) => {
                          setTodoListsInitialId(listId);
                          setShowTodoLists(true);
                        }}
                        readOnly={isToddlerMode && !isToddlerOwnBday}
                        tinted={true}
                        compact={!calendarOnly}
                        fadeTint={compactLayout}
                        kidLensMember={
                          // In toddler calendar-only mode we already
                          // filter the list to the toddler's events,
                          // so drop the kid-lens (which would repaint
                          // every card a single avatar colour) and let
                          // the normal category tints shine.
                          isToddlerMode && calendarOnly
                            ? null
                            : toddlerId
                              ? members.find((m) => m.id === toddlerId) || null
                              : null
                        }
                        bigAvatars={hasYoungKid(members)}
                        onChanged={(updated) =>
                          setUpcoming((prev) =>
                            prev.map((e) => (e.id === updated.id ? updated : e))
                          )
                        }
                      />
                    </React.Fragment>
                  );
                })}
                {/* Tail "open calendar" card — matches EventPill footprint so the
                    strip reads as one continuous row on mobile + wraps cleanly on desktop.
                    Hidden in toddler mode so the little one can't open the editable sheet. */}
                {!isToddlerMode && (
                  <button
                    type="button"
                    onClick={() => setShowCalendar(true)}
                    className="ff-card ff-card-hover text-left p-4 flex-shrink-0 flex-1 min-w-[180px] max-w-[280px] flex flex-col gap-2 relative items-start justify-between border-dashed"
                    style={{ borderStyle: "dashed" }}
                    data-testid="open-calendar-card"
                    aria-label={t({ en: "Open the family calendar", da: "Åbn familiekalenderen" })}
                  >
                    <div className="label-tiny">{t({ en: "Calendar", da: "Kalender" })}</div>
                    <div className="flex items-center gap-2">
                      <div className="w-14 h-14 rounded-full bg-[color:var(--bg)] border border-[color:var(--line-soft)] flex items-center justify-center">
                        <CalendarIcon className="w-6 h-6" strokeWidth={1.5} />
                      </div>
                      <div className="font-display-italic text-2xl leading-none tracking-tight">
                        {t({ en: "See all", da: "Se alle" })}
                      </div>
                    </div>
                    <div className="label-tiny inline-flex items-center gap-1 text-[color:var(--ink-2)]">
                      {t({ en: "Add, edit, filter", da: "Tilføj, ret, filtrér" })} <ArrowRight className="w-3 h-3" strokeWidth={2} />
                    </div>
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Removed — the "Don't forget" strip now renders just above
          the CalendarSheet/upcoming-events block so it's visible at
          the top of the dashboard instead of buried below the whole
          calendar view. See the `missableListsBlock` render above. */}

      {/* Member grid */}
      <main className="px-6 md:px-10 pb-20">
        <div className="max-w-6xl mx-auto">
          {!isToddlerMode && <InstallPwaPrompt />}
          {isToddlerMode && !calendarOnly && toddlerFunPicker}
          {/* Houses mode — either the front "which house?" screen with
              house tiles + a shared members row, or the inside view of
              a single active house (back-button at the top, normal big
              tiles below). Falls through to the classic grid when no
              houses exist on this family. */}
          {houses.length > 0 ? (
            onHouseFront ? (
              <HouseTiles
                houses={houses}
                sharedMembers={sharedMembers}
                memberCounts={membersByHouse}
                onEnter={enterHouse}
                renderSharedTile={(m, i) => renderBigTile(m, i)}
              />
            ) : calendarOnly ? (
              // Inside a house in calendar-only mode: events strip above
              // already handles this view. A member-tile grid would be
              // redundant (and in calendarOnly `visibleMembers` is
              // pet-only anyway, so the grid would often be "Nobody
              // lives here" even when Dad/Mum clearly do). Render the
              // header-only back button so the user can step back out.
              (() => {
                const activeHouse =
                  houses.find((h) => h.id === activeHouseId) || null;
                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl" aria-hidden="true">
                          {houseEmoji(
                            Math.max(
                              0,
                              [...houses]
                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                .findIndex((h) => h.id === activeHouseId),
                            ),
                          )}
                        </span>
                        <h2 className="font-display-italic text-2xl md:text-3xl leading-none tracking-tight">
                          {activeHouse?.name || t({ en: "Inside", da: "Indenfor" })}
                        </h2>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveHouseId(null)}
                        className="ff-btn text-sm"
                        data-testid="dashboard-back-to-houses"
                      >
                        {t({ en: "← Houses", da: "← Huse" })}
                      </button>
                    </div>
                    {activeHouse?.deletion_requested_at && (
                      <HouseDeletionBanner
                        house={activeHouse}
                        onDeleted={async () => {
                          setActiveHouseId(null);
                          try {
                            const fresh = await api.listHouses();
                            if (Array.isArray(fresh)) setHouses(fresh);
                          } catch {/* non-fatal */}
                        }}
                        onCancelled={async () => {
                          try {
                            const fresh = await api.listHouses();
                            if (Array.isArray(fresh)) setHouses(fresh);
                          } catch {/* non-fatal */}
                        }}
                      />
                    )}
                  </div>
                );
              })()
            ) : (
              (() => {
                // Inside a house: show its own members first, then any
                // shared kids/pets (both those assigned to another house
                // AND those left unassigned = "visible to all houses")
                // so cross-house visibility still works.
                const own = visibleMembers.filter(
                  (m) => m.house_id === activeHouseId,
                );
                const fromOthers = sharedMembers.filter(
                  (m) => (m.house_id || "") !== activeHouseId,
                );
                const inside = [...own, ...fromOthers];
                const activeHouse =
                  houses.find((h) => h.id === activeHouseId) || null;
                return (
                  <div
                    className="space-y-5"
                    data-testid={`dashboard-inside-${activeHouseId}`}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl" aria-hidden="true">
                          {houseEmoji(
                            Math.max(
                              0,
                              [...houses]
                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                .findIndex((h) => h.id === activeHouseId),
                            ),
                          )}
                        </span>
                        <h2 className="font-display-italic text-2xl md:text-3xl leading-none tracking-tight">
                          {activeHouse?.name || t({ en: "Inside", da: "Indenfor" })}
                        </h2>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveHouseId(null)}
                        className="ff-btn text-sm"
                        data-testid="dashboard-back-to-houses"
                      >
                        {t({ en: "← Houses", da: "← Huse" })}
                      </button>
                    </div>
                    {activeHouse?.deletion_requested_at && (
                      <HouseDeletionBanner
                        house={activeHouse}
                        onDeleted={async () => {
                          setActiveHouseId(null);
                          try {
                            const fresh = await api.listHouses();
                            if (Array.isArray(fresh)) setHouses(fresh);
                          } catch {/* non-fatal */}
                        }}
                        onCancelled={async () => {
                          try {
                            const fresh = await api.listHouses();
                            if (Array.isArray(fresh)) setHouses(fresh);
                          } catch {/* non-fatal */}
                        }}
                      />
                    )}
                    {inside.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                        {inside.map((m, i) => renderBigTile(m, i))}
                      </div>
                    ) : (
                      <div className="text-sm text-[color:var(--ink-3)] py-6">
                        Nobody's been moved into this house yet. Head to
                        Members to drop people in.
                      </div>
                    )}
                  </div>
                );
              })()
            )
          ) : visibleMembers.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
              {visibleMembers.map((m, i) => renderBigTile(m, i))}
            </div>
          ) : null}
        </div>
      </main>

      {selectedMember &&
        (selectedMember.role === "pet" ? (
          (selectedMember.pet_species && selectedMember.pet_species !== "dog") ? (
            <PetCareDialog
              pet={selectedMember}
              existing={checkinByMember[selectedMember.id]}
              members={members}
              viewerId={viewerId}
              tier={tier}
              onOpenUpgrade={() => {
                setSelectedMember(null);
                setBilling({ open: true, reason: "pet_care" });
              }}
              onPetUpdated={(updated) => {
                if (!updated?.id) return;
                setMembers((cur) =>
                  cur.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)),
                );
              }}
              onClose={() => setSelectedMember(null)}
              onSaved={(updated) => {
                setCheckins((prev) => {
                  const others = prev.filter(
                    (c) => c.member_id !== updated.member_id
                  );
                  return [...others, updated];
                });
              }}
            />
          ) : (
            <PetDialog
              pet={selectedMember}
              existing={checkinByMember[selectedMember.id]}
              members={members}
              viewerId={viewerId}
              tier={tier}
              onOpenUpgrade={() => {
                setSelectedMember(null);
                setBilling({ open: true, reason: "pet_care" });
              }}
              onPetUpdated={(updated) => {
                if (!updated?.id) return;
                setMembers((cur) =>
                  cur.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)),
                );
              }}
              onClose={() => setSelectedMember(null)}
              onKeepOpen={(updated) => {
                // Water / feed taps: update the checkin but keep the
                // dialog open so the user can log the companion action
                // without bouncing back to the dashboard.
                setCheckins((prev) => {
                  const others = prev.filter(
                    (c) => c.member_id !== updated.member_id,
                  );
                  return [...others, updated];
                });
              }}
              onSaved={(updated) => {
                setCheckins((prev) => {
                  const others = prev.filter(
                    (c) => c.member_id !== updated.member_id
                  );
                  return [...others, updated];
                });
                setSelectedMember(null);
              }}
            />
          )
        ) : isYoungKid(selectedMember) ? (
          <KidCheckinDialog
            member={selectedMember}
            existing={checkinByMember[selectedMember.id]}
            viewer={members.find((m) => m.id === viewerId) || null}
            onClose={() => setSelectedMember(null)}
            onSaved={(updated) => {
              setCheckins((prev) => {
                const others = prev.filter(
                  (c) => c.member_id !== updated.member_id
                );
                return [...others, updated];
              });
              setSelectedMember(null);
            }}
          />
        ) : (
          <CheckinDialog
            member={selectedMember}
            existing={checkinByMember[selectedMember.id]}
            isSelf={selectedMember.id === viewerId}
            settings={settings}
            onClose={() => setSelectedMember(null)}
            onSaved={(updated) => {
              setCheckins((prev) => {
                const others = prev.filter(
                  (c) => c.member_id !== updated.member_id
                );
                return [...others, updated];
              });
              setSelectedMember(null);
            }}
          />
        ))}
      {pinFor && (
        <PinDialog
          member={pinFor}
          onCancel={() => setPinFor(null)}
          onSuccess={() => {
            setViewerId(pinFor.id);
            setSelectedMember(pinFor);
            setPinFor(null);
          }}
        />
      )}

      {historyFor && (
        <HistoryDialog
          member={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}

      {showManager && (
        <MembersManager
          settings={settings}
          members={members}
          viewerId={viewerId}
          setViewerId={setViewerId}
          autoOpenAdd={managerAutoAdd}
          membersOnly={onHouseFront}
          onClose={() => {
            setShowManager(false);
            setManagerAutoAdd(false);
            // Houses may have been added/removed/renamed while settings were open
            api.listHouses().then(setHouses).catch(() => {});
          }}
          onChanged={async () => {
            await onRefresh();
            api.listHouses().then(setHouses).catch(() => {});
          }}
          setMembers={setMembers}
          setSettings={setSettings}
          onSignOut={onSignOut}
          onUpgradeNeeded={(reason) => {
            setShowManager(false);
            setBilling({ open: true, reason });
          }}
          onOpenBilling={() => {
            setShowManager(false);
            setBilling({ open: true, reason: null });
          }}
        />
      )}
      {showCalendar && !calendarOnly && (
        <CalendarSheet
          members={members}
          viewerId={viewerId}
          autoOpenAdd={calendarAutoAdd}
          scrollToEventId={calendarScrollTo}
          openEditEventId={calendarOpenEdit}
          hasBYOKey={!!settings?.byo_google_ai_key_set}
          onOpenSettings={() => {
            setShowManager(true);
            // Land on the Family tab so the BYO Google AI key control
            // is the first thing the user sees.
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent("famfeel:open-manager-tab", {
                  detail: { tab: "family" },
                }),
              );
            }, 0);
          }}
          requestDeleteEventId={calendarRequestDelete}
          onClose={() => {
            setShowCalendar(false);
            setCalendarAutoAdd(false);
            setCalendarScrollTo(null);
            setCalendarOpenEdit(null);
            setCalendarRequestDelete(null);
            loadUpcoming();
          }}
        />
      )}
      {showChores && (
        <ChoresSheet
          members={members}
          viewerId={viewerId}
          onClose={() => setShowChores(false)}
        />
      )}
      {showTodoLists && (
        <TodoListsSheet
          members={members}
          initialListId={todoListsInitialId}
          onClose={() => {
            setShowTodoLists(false);
            setTodoListsInitialId(null);
            // Refresh the "Don't forget" strip the instant the sheet
            // closes — without this, a list the user just created
            // with a due date wouldn't appear until the next 30s
            // poll tick.
            loadMissableLists();
          }}
          onOpenLinkedEvent={(eventId) => {
            // Banner click — leave lists, jump to the calendar at the
            // linked event's day group.
            setShowTodoLists(false);
            setTodoListsInitialId(null);
            setCalendarScrollTo(eventId);
            setShowCalendar(true);
            loadMissableLists();
          }}
        />
      )}
      <BillingSheet
        open={billing.open}
        reason={billing.reason}
        onClose={() => setBilling({ open: false, reason: null })}
      />
      {houseUnlockFor && (
        <HouseUnlockDialog
          houseId={houseUnlockFor.house.id}
          houseName={houseUnlockFor.house.name}
          onClose={() => setHouseUnlockFor(null)}
          onUnlocked={() => {
            const pending = houseUnlockFor.pendingMember;
            const enterAfter = houseUnlockFor.afterUnlock === "enter";
            const houseId = houseUnlockFor.house.id;
            setHouseUnlockFor(null);
            if (pending) openCheckin(pending);
            else if (enterAfter) setActiveHouseId(houseId);
          }}
        />
      )}
      {houseFirstPinFor && (
        <HouseFirstPinDialog
          house={houseFirstPinFor}
          onClose={() => setHouseFirstPinFor(null)}
          onDone={async () => {
            const h = houseFirstPinFor;
            setHouseFirstPinFor(null);
            // Refresh houses so has_pin flips true, then enter it.
            try {
              const fresh = await api.listHouses();
              if (Array.isArray(fresh)) setHouses(fresh);
            } catch {/* non-fatal */}
            setActiveHouseId(h.id);
          }}
        />
      )}
      {familyPinPrompt && (
        <FamilyPinDialog
          title="Open settings"
          subtitle="Parent-only area"
          onCancel={() => setFamilyPinPrompt(null)}
          onSuccess={() => {
            const fn = familyPinPrompt.onSuccess;
            setFamilyPinPrompt(null);
            if (typeof fn === "function") fn();
          }}
        />
      )}
      {showUnlock && (
        <FamilyPinDialog
          title="Exit toddler mode"
          subtitle="Enter the family PIN"
          onCancel={() => setShowUnlock(false)}
          onSuccess={() => {
            clearToddlerMode();
            setToddlerId(null);
            setShowUnlock(false);
            toast.success("Back to normal mode");
          }}
        />
      )}
      {funOpen && (
        <FunScreen
          variant={typeof funOpen === "string" ? funOpen : undefined}
          onClose={() => setFunOpen(false)}
        />
      )}
      <InstallCelebrationBanner />
      <QuickGuideDialog
        guide={openGuide}
        open={!!openGuide}
        onClose={() => setOpenGuide(null)}
      />
      {peekFor && peekFor.member.role === "pet" && (
        <PetPeek
          pet={peekFor.member}
          anchorRect={peekFor.rect}
          onClose={() => setPeekFor(null)}
        />
      )}
      {peekFor && peekFor.member.role !== "pet" && (
        <MoodPeek
          member={peekFor.member}
          anchorRect={peekFor.rect}
          onClose={() => setPeekFor(null)}
        />
      )}
      {bdayCountdown && bdayCountdown.member && (
        <BirthdayCountdown
          member={bdayCountdown.member}
          age={bdayCountdown.age}
          daysUntil={bdayCountdown.daysUntil}
          onClose={() => setBdayCountdown(null)}
        />
      )}
      {/* Celebrate-burst — replays on every famfeel:celebrate CustomEvent
          via the `celebrateKey` trick. Renders over the dashboard as
          a one-shot; the ConfettiBurst component cleans itself up. */}
      {celebrateKey > 0 && <ConfettiBurst key={celebrateKey} />}
      {showWhosFree && (
        <WhosFreeDialog
          members={members}
          fallbackEvents={upcoming}
          onClose={() => setShowWhosFree(false)}
        />
      )}
    </div>
  );
}
